import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import {
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Space,
  Tooltip,
  Tag,
  Switch,
  Cascader,
  Modal,
  Badge,
  TreeSelect,
  Alert,
} from 'antd';
import { toast } from 'react-toastify';
import { Edit3, Package, Trash2, Lock, PlusCircle, MinusCircle, Info } from 'lucide-react';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import useAppSetting, { useMaterialTypeAlias, useIngredientsAlias } from '@/hooks/useAppSetting';
import {
  get_list_of_setting,
  get_machine_category_with_groups,
  get_ingredient_from_machine_categories_by_op,
  get_machine_spec_values,
  bp_units_data,
  get_recipe_variant_list,
  get_recipe_bom_item_list,
  get_formula_definitions,
  get_recipe_meta_form,
  get_output_form_type_by_id,
} from '@/utils/api';
import { get_recipe_ingredient_remaining } from './mbmr_api';
import { check_material_machine_capacity, get_process_control_parameter } from '@/utils/api';
import {
  parseMachineIdsList,
  buildCascaderLeafValue,
  resolveMachineDetails,
  LockedMachineSetupPanel,
  inferTransferAnchor,
  normalizeMovementKey,
} from './machineRoutingHelpers';

const { TextArea } = Input;
const buildValidationSchema = (label = 'Materials') => Yup.object().shape({
  movement_type: Yup.string().nullable().required('Movement type is required'),
  material_type: Yup.string().nullable().required('Material type is required'),
  ingredient_id: Yup.array().nullable().when('material_type', {
    is: (v) => v !== 'WIPM',
    then: (s) => s.min(1, `At least one ${label.toLowerCase()} is required`).required(`${label} is required`),
  }),
  ingredient_name_manual: Yup.string().nullable().when('material_type', {
    is: 'WIPM',
    then: (s) => s.required(`${label} name is required`),
  }),
  quantity: Yup.number().nullable().when(['material_type', 'carry_over_type'], {
    is: (material_type, carry_over_type) =>
      material_type !== 'WIPM' && !carry_over_type,
    then: (s) => s.required('Quantity is required'),
  }),
  // 2026-09-02, "saving error here" — a BOM-linked row's Allowed Deviation field is
  // disabled and shows the linked BOM line's tolerance_percent (see the form below),
  // but that's read-only display — it was never written into values.deviation, and a
  // BOM line legitimately can have no tolerance set yet. required() still fired in
  // both cases, permanently blocking save with no way to fill the field in. The real
  // deviation value for a BOM-linked row is authoritative on the BOM line, not here
  // (apply_bom_tolerance_to_deviation syncs it server-side on save regardless of what's
  // submitted) — so skip requiring it whenever a BOM rule governs this row.
  deviation: Yup.number().nullable().when(['carry_over_type', 'rule_type', 'recipe_bom_item_id'], {
    is: (carry_over_type, rule_type, recipe_bom_item_id) => !carry_over_type && !rule_type && !recipe_bom_item_id,
    then: (s) => s.required('Deviation is required'),
  }),
  is_percentage: Yup.boolean().nullable().required('Select type'),
  parameter_id: Yup.string().nullable(),
  min_value: Yup.number().nullable().when('parameter_id', {
    is: (val) => val && val !== '',
    then: (schema) => schema.required('Min value required'),
  }),
  max_value: Yup.number().nullable()
    .when('parameter_id', {
      is: (val) => val && val !== '',
      then: (schema) => schema.required('Max value required'),
    }),
  from_machine_id: Yup.string().nullable(),
  to_machine_id: Yup.string().nullable(),
  machine_registers: Yup.array().nullable(),
  instruction: Yup.string().nullable(),
  execution_mode: Yup.string().nullable().required('Choose Execution Mode'),
  is_rm_code: Yup.boolean().nullable(),
  is_quantity: Yup.boolean().nullable(),
});

// Which verification methods an Execution Mode allows — mirrors IngredientsRules.js
// so the rule's constraints (e.g. Automatic disables QR Scan / Barcode) hold at
// execution time too, not just in the rule builder.
const METHOD_AVAILABILITY = {
  Manual: { qr_scan: true, barcode: true, scale_weight: true },
  'Semi-Automatic': { qr_scan: true, barcode: true, scale_weight: true },
  Automatic: { qr_scan: false, barcode: false, scale_weight: true },
};

// BOM Rule Type — 14 fixed types, each with real (server-side) calculation
// math behind it. See djangoapp/utilities/bom_calculation.py for the engine.
const RULE_TYPE_OPTIONS = [
  { value: 'PER_UNIT', label: 'Per Unit (e.g. mg/tablet)' },
  { value: 'PER_VOLUME', label: 'Per Volume (e.g. mg/mL)' },
  { value: 'PER_MASS', label: 'Per Mass (e.g. mg/g)' },
  { value: 'PERCENT_WW', label: '% w/w' },
  { value: 'PERCENT_WV', label: '% w/v' },
  { value: 'PERCENT_VV', label: '% v/v' },
  { value: 'FIXED_BATCH_QTY', label: 'Fixed' },
  { value: 'QS_FINAL_VOLUME', label: 'QS to Final Volume' },
  { value: 'QS_FINAL_WEIGHT', label: 'QS to Final Weight' },
  { value: 'QS_TARGET_PARAMETER', label: 'QS to Target Parameter' },
  { value: 'QS_PROCESS', label: 'QS as Required' },
  { value: 'FIXED_COUNT', label: 'Count' },
];
const PER_OUTPUT_UNIT_RULE_TYPES = ['PER_UNIT', 'FIXED_COUNT'];
const QS_RULE_TYPES = ['QS_FINAL_VOLUME', 'QS_FINAL_WEIGHT', 'QS_TARGET_PARAMETER', 'QS_PROCESS'];

const normalizeBomMaterialType = (rawValue) => {
  const value = String(rawValue || '').trim().toLowerCase();
  if (!value) return 'RM';
  if (value.includes('wip')) return 'WIPM';
  if (value.includes('intermediate')) return 'Intermediate';
  if (value.includes('product')) return 'Product';
  if (value.includes('pack')) return 'PM';
  if (
    value.includes('raw')
    || value.includes('ingredient')
    || value.includes('api')
    || value.includes('excipient')
    || value.includes('rm')
  ) {
    return 'RM';
  }
  return rawValue;
};

const getQuantityStrategyFromRuleType = (ruleType) => {
  if (QS_RULE_TYPES.includes(ruleType)) return 'qs';
  if (['FIXED_BATCH_QTY', 'FIXED_COUNT'].includes(ruleType)) return 'static';
  return 'scale_with_batch';
};

// Deviation rule options (shared with Monitoring) — when a value is treated as a deviation.
const DEVIATION_RULE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'less_than_min', label: 'Less than Min' },
  { value: 'greater_than_max', label: 'Greater than Max' },
  { value: 'less_than_min_or_greater_than_max', label: 'Less than Min or Greater than Max' },
];

export default function Ingredients({
  editIndex,
  onSubmit = (values) => {
    console.log('Submitted values:', values);
  },
  onQuantityChange,
  remainingQty,
  // Recipe-level mode: true when the recipe is input-based (track a configured ingredient),
  // false when it tracks the master batch quantity.
  isInputBasedRecipe = false,
  configuredIngredientId = null,
  configuredIngredientName = null,
  // Whether this operation has a "Product" sub-operation. When false, Discharge
  // Material Type is restricted to WIPM only.
  hasProductSubop = false,
  // Single-counter model from the parent. Live remaining = committed − (entered − original),
  // matching the header exactly — for both the configured ingredient and the master batch.
  committedConfigured = null,
  editingRowConfiguredOriginal = 0,
  committedMaster = null,
  editingRowMasterOriginal = 0,
  inventory_list,
  parameter_list,
  machine_list,
  machine_type_list = [],
  app_settings: appSettingsProp,
  lockedRuleFields,
  // Carry Over context (passed from parent when available)
  current_ingredients = [], // [{ ingredient_id, ingredient }] — ingredients already added in THIS operation
  previous_operations = [], // [{ operation_number, operation_name }] — already filtered by parent to selected dependencies
  has_monitoring_subop = false,
  // One Machine Per Operation rule
  one_machine_rule_enabled = false,
  // Whether the parent Operation Type is marked critical (drives the CRITICAL MATERIAL badge)
  operation_is_critical = false,
  configured_machines = [],
  machine_setup_entries = [],
  // Recipe context — used by the Carry Over → Previous Operation machine lookup
  recipe_id = null,
  // Code context — used to auto-generate `ingredient_code` (e.g., "PROC01_OP01_I03")
  process_code = '',
  operation_type_code = '',
  process_id,
  values = {
    movement_type: null,
    material_type: 'RM',
    ingredient_id: [],
    ingredient: [],
    ingredient_name_manual: null,
    unit: null,
    unit_id: null,
    quantity: null,
    lot_strategy: 'Multi Lot',
    number_of_lots: null,
    // 'max_fill' | 'uniform' | null — null means "use the plant default" (Application
    // Settings → Uniform Lot Sizing). Set explicitly only when this material overrides it.
    fill_strategy: null,
    deviation: null,
    is_percentage: false,
    parameter_id: null,
    parameter: null,
    parameter_unit: null,
    min_value: null,
    max_value: null,
    deviation_rule: 'none',
    from_machine_id: null,
    from_machine_name: null,
    to_machine_id: null,
    to_machine_name: null,
    instruction: null,
    machine_registers: [],
    execution_mode: 'Manual',
    verification_mode: 'QR Verification',
    multi_lot_strategy: 'Multi Lot Allowed',
    is_rm_code: false,
    is_quantity: false,
    carry_over_type: null, // "current" | "previous" | null
    carry_over_prev_operation_ids: [],
    carry_over_ref_ingredient_ids: [],
    // Editable Runtime Compliance Rules (defaults seeded from the rule). Saved per ingredient.
    compliance_rules: null,
    // Editable Runtime Material Execution Policy. Saved per ingredient (separate JSON).
    execution_policy: null,
  },
}) {
  const rulesEnabled = useAppSetting('Rules');
  // These three rule types vary a lot by client (different units/parameters
  // per plant) — switchable off in Application Settings. Default on.
  const perVolumeEnabled = useAppSetting('Enable Per Volume Rule', true);
  const perMassEnabled = useAppSetting('Enable Per Mass Rule', true);
  const qsTargetParameterEnabled = useAppSetting('Enable QS Target Parameter Rule', true);
  // Plant-wide default lot-splitting strategy (Application Settings → Uniform Lot
  // Sizing) when a material's quantity exceeds a destination machine's capacity —
  // off = max-fill. A material can still override it via values.fill_strategy.
  const uniformLotSizingDefault = useAppSetting('Uniform Lot Sizing', false);
  const disabledRuleTypes = new Set([
    ...(perVolumeEnabled ? [] : ['PER_VOLUME']),
    ...(perMassEnabled ? [] : ['PER_MASS']),
    ...(qsTargetParameterEnabled ? [] : ['QS_TARGET_PARAMETER']),
  ]);
  const enabledRuleTypeOptions = RULE_TYPE_OPTIONS.filter((o) => !disabledRuleTypes.has(o.value));
  // Maps a material-type name to its configured alias (Application Settings →
  // "Material Type Alias"). Used to relabel the "Ingredient" field.
  const materialTypeAlias = useMaterialTypeAlias();
  const ingredientsAlias = useIngredientsAlias();
  const ingredientLabel = materialTypeAlias('Ingredients') || ingredientsAlias;
  const [appSettings, setAppSettings] = useState(appSettingsProp || []);
  // Internal tracker: ingredients submitted via this form during the current session.
  // Used as a fallback when the parent doesn't pass `current_ingredients`.
  const [locallyAddedIngredients, setLocallyAddedIngredients] = useState([]);
  // BOM Rule Type support data — units for Rule UOM, this recipe's variants
  // (for PER_UNIT/FIXED_COUNT), active formulas (for the correction layer).
  const [ruleUnitList, setRuleUnitList] = useState([]);
  const [recipeVariantList, setRecipeVariantList] = useState([]);
  const [activeFormulaList, setActiveFormulaList] = useState([]);
  // Recipe-level BOM list (authored in the Edit Recipe modal's BOM tab) —
  // picking one here copies its material + rule fields onto this ingredient
  // row (still independently editable after).
  const [recipeBomItemList, setRecipeBomItemList] = useState([]);
  // Recipe's Output Form unit_categories — narrows the Rule UOM picker below
  // to units that actually make sense for what this recipe produces (e.g.
  // Liquid -> Volume only). Empty/unset = unrestricted.
  const [ruleUomCategories, setRuleUomCategories] = useState([]);
  const [showBomRule, setShowBomRule] = useState(!!(values.rule_type || values.recipe_bom_item_id));

  useEffect(() => {
    bp_units_data((res) => {
      if (res?.data?.status === 'success') setRuleUnitList(res?.data?.data || []);
    });
    get_formula_definitions((data) => {
      setActiveFormulaList((Array.isArray(data) ? data : []).filter((f) => f.status === 'active'));
    });
    if (recipe_id) {
      get_recipe_variant_list((res) => {
        if (res?.data?.status === 'success') setRecipeVariantList(res?.data?.data || []);
      }, { recipe_id });
      get_recipe_bom_item_list((res) => {
        if (res?.data?.status === 'success') setRecipeBomItemList(res?.data?.data || []);
      }, { recipe_id });
      get_recipe_meta_form((res) => {
        const outputFormTypeId = res?.data?.data?.output_form_id;
        if (res?.data?.status === 'success' && outputFormTypeId) {
          get_output_form_type_by_id((oftRes) => {
            if (oftRes?.data?.status === 'success') {
              setRuleUomCategories(oftRes?.data?.unit_categories || []);
            }
          }, { output_form_type_id: outputFormTypeId });
        }
      }, { recipe_id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe_id]);

  const ruleUomOptions = (ruleUomCategories.length
    ? ruleUnitList.filter((u) => ruleUomCategories.includes(u.unit_category))
    : ruleUnitList
  ).map((u) => ({ label: u.unit_name, value: u.id }));
  // Remaining quantity info fetched from recipe metadata for the currently selected ingredient
  const [ingredientRemainingInfo, setIngredientRemainingInfo] = useState(null);
  const [showConditionalParam, setShowConditionalParam] = useState(!!(values.parameter_id));

  useEffect(() => {
    setShowConditionalParam(!!(values.parameter_id));
    // Reset first, then refetch the configured remaining for the edited row's
    // ingredient — the Select onChange doesn't fire when opening an existing row,
    // so without this the configured Cfg/Used/Rem info would be blank in edit mode.
    setIngredientRemainingInfo(null);
    const editIds = Array.isArray(values.ingredient_id) ? values.ingredient_id : [];
    if (recipe_id && editIds.length === 1) {
      get_recipe_ingredient_remaining(
        (res) => {
          if (res?.data?.status === 'success') {
            setIngredientRemainingInfo(res.data.data);
          }
        },
        { recipe_id, material_id: editIds[0] }
      );
    }
  }, [editIndex]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fromMachineType, setFromMachineType] = useState(null);
  const [toMachineType, setToMachineType] = useState(null);
  const [fromMachineIds, setFromMachineIds] = useState([]);
  const [toMachineIds, setToMachineIds] = useState([]);
  // Result of the last check_material_machine_capacity call — does this
  // row's quantity fit the destination machine(s)' Usable Capacity?
  const [capacityCheck, setCapacityCheck] = useState(null);
  const [capacityCheckLoading, setCapacityCheckLoading] = useState(false);
  // 2026-09-02, "equipment showing below ... make that clickable ... not
  // removable only clickable to see it" — clicking a Destination Equipment
  // chip scrolls to + briefly highlights that machine's row in the Lot
  // Distribution panel below (view-only, no removal). "there is no primary or
  // secondary machine" — every chip is equal; clicking one re-runs the
  // capacity check scoped to just that machine (a scenario preview: how much,
  // how many lots, through THIS one) without touching to_machine_ids/save at
  // all. scrollTargetMachineId tracks which machine's row to jump to once
  // that (async) preview lands and its row actually exists in the DOM.
  const [highlightedMachineId, setHighlightedMachineId] = useState(null);
  const [scrollTargetMachineId, setScrollTargetMachineId] = useState(null);
  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);
  const [machineCapacityMap, setMachineCapacityMap] = useState({});
  const [scaleRegisters, setScaleRegisters] = useState([]);
  const [scaleRegistersLoading, setScaleRegistersLoading] = useState(false);
  // Machine category+group list filtered to the picked previous op's ingredient from-machines
  const [prevOpFromMachines, setPrevOpFromMachines] = useState([]);

  // Build 3-level cascader options: category → group → machine
  const buildMachineCascaderOptions = (source) =>
    (source || []).map((cat) => ({
      label: cat.machine_category_name,
      value: cat.machine_category_id,
      children: (cat.machine_groups || []).map((g) => {
        const machines = g.machines || [];
        return {
          label: g.machine_group_name,
          value: g.machine_group_id,
          _catId: cat.machine_category_id,
          _catLabel: cat.machine_category_name,
          _grpId: g.machine_group_id,
          _grpLabel: g.machine_group_name,
          _machineId: null,
          _allIds: machines.map((m) => m.machine_id),
          children: [
            {
              label: `All Machines (${machines.length})`,
              value: '__all__',
              _catId: cat.machine_category_id,
              _catLabel: cat.machine_category_name,
              _grpId: g.machine_group_id,
              _grpLabel: g.machine_group_name,
              _machineId: machines[0]?.machine_id ?? null,
              _allIds: machines.map((m) => m.machine_id),
            },
            ...machines.map((m) => ({
              label: m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name,
              value: m.machine_id,
              _catId: cat.machine_category_id,
              _catLabel: cat.machine_category_name,
              _grpId: g.machine_group_id,
              _grpLabel: g.machine_group_name,
              _machineId: m.machine_id,
              _allIds: [m.machine_id],
            })),
          ],
        };
      }),
    }));

  // 2026-09-02, "make it for group basis and multi selection of machine not one, in
  // reality we can use multiple and runtime we use only one" — Scale Machine Config
  // stops at Category -> Group (unlike Destination/Source Equipment's 3-level
  // cascader); which specific machine(s) within that group are viable scales is a
  // separate multi-toggle below, not a single drilled-down leaf.
  const buildMachineGroupCascaderOptions = (source) =>
    (source || []).map((cat) => ({
      label: cat.machine_category_name,
      value: cat.machine_category_id,
      children: (cat.machine_groups || []).map((g) => ({
        label: g.machine_group_name,
        value: g.machine_group_id,
        _catId: cat.machine_category_id,
        _grpId: g.machine_group_id,
        isLeaf: true,
      })),
    }));

  // Flat id -> machine row lookup, for turning a group-only selection's
  // silent _allIds list into something visible ("3 machines: BPH1, BPH2,
  // BPH3") instead of the cascader path being the only feedback.
  const resolveMachines = (ids) => {
    if (!ids?.length) return [];
    const flat = (machineTypeWithGroups || []).flatMap((cat) =>
      (cat.machine_groups || []).flatMap((g) => g.machines || [])
    );
    return ids
      .map((id) => flat.find((m) => String(m.machine_id) === String(id)))
      .filter(Boolean);
  };

  const getMachineMasterRow = (machineId) =>
    (machine_list || []).find((row) => String(row.machine_id || row.id) === String(machineId));

  const getResolvedMachineCapacity = (machineId) => {
    const specRow = machineCapacityMap[machineId];
    if (specRow?.value !== undefined && specRow?.value !== null) {
      return {
        label: specRow.name || 'Capacity',
        value: specRow.value,
        unit: specRow.uom_name || '',
      };
    }
    const machine = getMachineMasterRow(machineId);
    if (!machine) return null;
    const specs = machine.technical_specifications || {};
    const value = specs.design_capacity ?? machine.max_quantity ?? '';
    const unit = specs.capacity_unit || '';
    if (value === '' || value === null || value === undefined) return null;
    return {
      label: 'Capacity',
      value,
      unit,
    };
  };

  const getMachineCapacityText = (machineId) => {
    const capacity = getResolvedMachineCapacity(machineId);
    if (!capacity) return '';
    return `${capacity.label}: ${capacity.value}${capacity.unit ? ` ${capacity.unit}` : ''}`;
  };

  // Chip text stays compact (code only, falling back to name) so a handful
  // of selections don't blow out the row width; the full "Name (Code)" only
  // shows on hover. Kept in sync with LockedMachineSetupPanel's chip row
  // (machineRoutingHelpers.js) so a selected-machine chip looks identical
  // whether the from/to control is a free Cascader or locked to a Machine
  // Setup rule (e.g. Dispensing vs. other movement types shouldn't render
  // this differently).
  const machineChipLabel = (m) => (m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name);
  const machineChipShort = (m) => m.machine_code || m.machine_name;
  const machineChipTooltipText = (m) => {
    const label = machineChipLabel(m);
    const capacity = getMachineCapacityText(m.machine_id);
    return capacity ? `${label} • ${capacity}` : label;
  };
  const machineChipTooltip = (m) => {
    const label = machineChipLabel(m);
    const capacity = getMachineCapacityText(m.machine_id);
    if (!capacity) return label;
    return (
      <div className="flex flex-col leading-5">
        <span>{label}</span>
        <span>{capacity}</span>
      </div>
    );
  };

  useEffect(() => {
    if (appSettingsProp?.length) {
      setAppSettings(appSettingsProp);
    } else {
      get_list_of_setting((res) => {
        if (res?.data?.data) setAppSettings(res.data.data);
      });
    }
  }, [appSettingsProp]);

  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    }, process_id);
  }, [process_id]);

  // Prefill registers ONLY for the initial "editing an already-saved ingredient that
  // has scale_machine_ids set" case — this reads the OUTER `values` prop (the incoming
  // record), not Formik's live internal state, so it does NOT and should NOT react to
  // a live toggle click during the current session; toggleScaleMachine (inside the
  // Formik render prop, where setFieldValue's real state actually lives) does that
  // fetch itself. 2026-09-02, "still same" was this exact effect being relied on for
  // live clicks — it never could, since it never even reruns for them.
  useEffect(() => {
    const machId = values?.scale_machine_ids?.[0];
    if (!machId) { setScaleRegisters([]); return; }
    setScaleRegistersLoading(true);
    // 2026-09-02, "but there are pcs', see and check" — this used to call
    // get_register_by_machine (machine_parameter_tag), which is empty for these
    // machines; the real, populated data (what "Control & Monitoring/Reading
    // Parameters" on Equipment Master actually shows) lives in
    // ProcessControlParameter, fetched via get_process_control_parameter.
    get_process_control_parameter(
      (res) => { setScaleRegisters(res?.data?.data || []); setScaleRegistersLoading(false); },
      null,
      machId,
    );
  }, [values?.scale_machine_ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync cascader state when editing or when machineTypeWithGroups loads
  useEffect(() => {
    if (!machineTypeWithGroups.length) return;
    const resolve = (categoryId, groupId, machineIds) => {
      if (!categoryId && !groupId) return null;
      // If only group id captured, find category
      if (!categoryId && groupId) {
        for (const cat of machineTypeWithGroups) {
          const grp = cat.machine_groups?.find((g) => String(g.machine_group_id) === String(groupId));
          if (grp) {
            const groupMachineIds = (grp.machines || []).map((m) => Number(m.machine_id));
            const leaf = buildCascaderLeafValue(machineIds, groupMachineIds);
            return [cat.machine_category_id, grp.machine_group_id, leaf];
          }
        }
        return null;
      }
      const cat = machineTypeWithGroups.find((c) => String(c.machine_category_id) === String(categoryId));
      if (!cat) return null;
      if (groupId) {
        const grp = cat.machine_groups?.find((g) => String(g.machine_group_id) === String(groupId));
        if (grp) {
          const groupMachineIds = (grp.machines || []).map((m) => Number(m.machine_id));
          const allSelected = groupMachineIds.length > 0
            && machineIds.length === groupMachineIds.length
            && groupMachineIds.every((id) => machineIds.includes(id));
          if (allSelected) {
            return [cat.machine_category_id, grp.machine_group_id];
          }
          const leaf = buildCascaderLeafValue(machineIds, groupMachineIds);
          return [cat.machine_category_id, grp.machine_group_id, leaf];
        }
      }
      return [cat.machine_category_id];
    };
    // Apply fallback per movement-type direction
    const mv = normalizeMovementKey(values.movement_type || values.sub_operation_type);
    const dischargeLocked = one_machine_rule_enabled && mv === 'discharge';
    const destinationLocked = one_machine_rule_enabled && ['charging', 'dispensing'].includes(mv);
    const setupCat = machine_setup_entries?.[0]?.machine_type_id;
    const setupGrp = machine_setup_entries?.[0]?.machine_group_id;
    setFromMachineType(
      resolve(
        values.from_machine_category_id || (dischargeLocked ? setupCat : null),
        values.from_machine_group_id || (dischargeLocked ? setupGrp : null),
        parseMachineIdsList(
          values.from_machine_ids,
          values.from_machine_ids_csv,
          values.from_machine_id && values.from_machine_id !== values.from_machine_category_id
            ? values.from_machine_id : null
        )
      )
    );
    setToMachineType(
      resolve(
        values.to_machine_category_id || (destinationLocked ? setupCat : null),
        values.to_machine_group_id || (destinationLocked ? setupGrp : null),
        parseMachineIdsList(
          values.to_machine_ids,
          values.to_machine_ids_csv,
          values.to_machine_id && values.to_machine_id !== values.to_machine_category_id
            ? values.to_machine_id : null
        )
      )
    );
    setFromMachineIds(parseMachineIdsList(values.from_machine_ids, values.from_machine_ids_csv, values.from_machine_id));
    setToMachineIds(parseMachineIdsList(values.to_machine_ids, values.to_machine_ids_csv, values.to_machine_id));
  }, [
    values.from_machine_id,
    values.from_machine_ids,
    values.from_machine_ids_csv,
    values.from_machine_category_id,
    values.from_machine_group_id,
    values.to_machine_id,
    values.to_machine_ids,
    values.to_machine_ids_csv,
    values.to_machine_category_id,
    values.to_machine_group_id,
    values.movement_type,
    values.sub_operation_type,
    machineTypeWithGroups,
    one_machine_rule_enabled,
    machine_setup_entries,
  ]);

  // 2026-09-02, "where does it show? I don't see it" — an already-filled material row
  // (edit mode, or a copy that already has ingredient/quantity/machines set) never
  // triggered the capacity check at all: runCapacityCheck (below, in the Formik render
  // scope) only fires from live onChange handlers — ingredient picked, quantity typed,
  // machine selected — never just from data arriving already filled in. Runs the same
  // check once directly off the incoming `values` prop (not Formik's live state, which
  // is what those onChange calls already cover), so the capacity/Lot Distribution panel
  // shows up for a row that was already configured, not only one being actively edited.
  useEffect(() => {
    const ingredientId = values.ingredient_id?.[0];
    const quantity = values.quantity;
    const unitId = values.unit_id;
    const machineIds = parseMachineIdsList(values.to_machine_ids, values.to_machine_ids_csv, values.to_machine_id);
    if (!ingredientId || !quantity || !unitId || !machineIds?.length) return;
    setCapacityCheckLoading(true);
    check_material_machine_capacity(
      {
        material_inventory_id: ingredientId,
        quantity,
        unit_id: unitId,
        machine_ids: machineIds,
        number_of_lots: values.number_of_lots || undefined,
        fill_strategy: values.fill_strategy || (uniformLotSizingDefault ? 'uniform' : 'max_fill'),
      },
      (res) => {
        setCapacityCheckLoading(false);
        if (res?.status === 'failed') return;
        setCapacityCheck(res?.data || null);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.ingredient_id, values.quantity, values.unit_id, values.to_machine_id, values.to_machine_ids, values.to_machine_ids_csv]);

  // 2026-09-02, "should be able to click all of them ... on click calculation show
  // bottom and right" — after a chip click reruns the check scoped to just that one
  // machine (see the click handler below), its row doesn't exist in the DOM yet —
  // the capacityCheck state update + re-render is async — so wait for it to actually
  // appear before scrolling to/highlighting it.
  useEffect(() => {
    if (!scrollTargetMachineId) return;
    const el = document.getElementById(`lot-distribution-machine-${scrollTargetMachineId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const targetId = scrollTargetMachineId;
    setHighlightedMachineId(targetId);
    setScrollTargetMachineId(null);
    setTimeout(() => setHighlightedMachineId((cur) => (cur === targetId ? null : cur)), 1500);
  }, [capacityCheck, scrollTargetMachineId]);

  // Transform register to machine_registers when editing
  // If ingredient/parameter names are not provided, look them up
  const ingredientData =
    values.ingredient_id && !values.ingredient
      ? inventory_list?.find((i) => i.material_id === values.ingredient_id)
      : null;

  const parameterData =
    values.parameter_id && !values.parameter
      ? parameter_list?.find((p) => p.id === values.parameter_id)
      : null;
  const setupEntry = machine_setup_entries?.[0] || {};
  const setupMachineIds = Array.isArray(configured_machines) && configured_machines.length
    ? configured_machines.map(Number).filter(Boolean)
    : Array.isArray(setupEntry.machine_ids) && setupEntry.machine_ids.length
      ? setupEntry.machine_ids.map(Number).filter(Boolean)
      : setupEntry.machine_ids_csv
        ? String(setupEntry.machine_ids_csv).split(',').map(Number).filter(Boolean)
        : (setupEntry.machine_id ? [Number(setupEntry.machine_id)].filter(Boolean) : []);
  const setupMachineId = setupMachineIds[0] || null;
  const setupMachineDetails = resolveMachineDetails(
    setupMachineIds,
    machineTypeWithGroups,
    setupEntry
  );
  const capacityMachineIds = Array.from(
    new Set([
      ...setupMachineIds,
      ...fromMachineIds,
      ...toMachineIds,
      ...(values.scale_machine_ids || []),
    ].map(Number).filter(Boolean))
  );
  const capacityMachineIdsKey = capacityMachineIds.join(',');

  useEffect(() => {
    if (!capacityMachineIds.length) return;
    capacityMachineIds.forEach((machineId) => {
      if (machineCapacityMap[machineId] !== undefined) return;
      get_machine_spec_values(
        machineId,
        (res) => {
          const rows = res?.data?.data || [];
          const ceiling = rows.find((row) => row.validation_role === 'max_limit' && row.value != null);
          setMachineCapacityMap((prev) => ({ ...prev, [machineId]: ceiling || null }));
        },
        'CAPACITY'
      );
    });
  }, [capacityMachineIds, capacityMachineIdsKey, machineCapacityMap]);

  const setupMachineDetailsWithCapacity = {
    ...setupMachineDetails,
    machines: (setupMachineDetails.machines || []).map((machine) => {
      const capacity = getMachineCapacityText(machine.id);
      return {
        ...machine,
        tooltipText: capacity ? `${machine.label} • ${capacity}` : machine.label,
        tooltipTitle: capacity ? (
          <div className="flex flex-col leading-5">
            <span>{machine.label}</span>
            <span>{capacity}</span>
          </div>
        ) : machine.label,
      };
    }),
  };
  const hasRuleValue = (key) =>
    lockedRuleFields?.[key] !== undefined &&
    lockedRuleFields?.[key] !== null &&
    lockedRuleFields?.[key] !== '';
  const isRuleFieldLocked = (key) =>
    hasRuleValue(key) && lockedRuleFields?.[`${key}_editable`] !== true;
  const isComplianceRuleLocked = (key) =>
    hasRuleValue(key) && lockedRuleFields?.[`${key}_editable`] !== true;
  const initialMovementType =
    lockedRuleFields?.movement_type ||
    lockedRuleFields?.sub_operation_type ||
    values.movement_type ||
    values.sub_operation_type ||
    null;
  const initialMovementKey = normalizeMovementKey(initialMovementType);

  const initialValues = {
    ...values,
    // Map API keys back to form fields on edit
    // Saved ingredient rows carry critical_rm as a flat legacy field (not nested
    // under compliance_rules), so on edit values.compliance_rules is undefined
    // and the toggle silently fell back to the rule default instead of the
    // ingredient's own persisted value. Seed it in here (unless compliance_rules
    // already has its own critical_rm, e.g. after a later in-form toggle).
    compliance_rules: {
      ...(values.compliance_rules || {}),
      ...(values.compliance_rules?.critical_rm === undefined && values.critical_rm !== undefined
        ? { critical_rm: !!values.critical_rm }
        : {}),
    },
    scale_machine_type_id: values.scale_machine_type_id || null,
    scale_machine_group_id: values.scale_machine_group_id || null,
    scale_machine_ids: values.scale_machine_ids || [],
    scale_process_controls: values.scale_process_controls || [],
    // 2026-09-02, "use those right side rules part" — charging_mode_qr/charging_mode_scale/
    // fill_strategy all live inside the ingredient's own execution_policy JSON (see
    // resolvedExecutionPolicy below), NOT as flat top-level keys — values.charging_mode_qr
    // was reading a key the GET response never actually returned (execution_policy wasn't
    // in the response dict at all until now), so these two always silently reset to their
    // hardcoded defaults on every edit regardless of what was saved. Now reads the real
    // nested value; lot_strategy/number_of_lots are unaffected (already their own real
    // dedicated columns, already round-tripping correctly).
    charging_mode_qr: values.execution_policy?.charging_mode_qr || lockedRuleFields?.charging_mode_qr || 'Single QR',
    charging_mode_scale: values.execution_policy?.charging_mode_scale || lockedRuleFields?.charging_mode_scale || 'Single Stretch',
    lot_strategy: values.lot_strategy || lockedRuleFields?.lot_strategy || 'Multi Lot',
    number_of_lots: values.number_of_lots ?? null,
    // 'max_fill' | 'uniform' | null — null means "use the plant default" (Application
    // Settings → Uniform Lot Sizing), same meaning as the initialValues default below.
    fill_strategy: values.execution_policy?.fill_strategy ?? lockedRuleFields?.fill_strategy ?? null,
    movement_type: initialMovementType,
    material_type:
      lockedRuleFields?.material_type ||
      values.material_type ||
      (values.is_work_in_progress_material ? 'WIPM' : 'RM'),
    ingredient_name_manual: values.ingredient_name_manual || values.work_in_progress_material_name || null,
    quantity_text: values.quantity_text || (values.is_work_in_progress_quantity ? '↩ Quantity carried over from previous operation' : null),
    // Convert 'register' from API to 'machine_registers' for form
    machine_registers: values.register || values.machine_registers || [],
    // Ensure all required fields exist and handle type conversion
    ingredient_id: Array.isArray(values.ingredient_id)
      ? values.ingredient_id
      : values.ingredient_id ? [values.ingredient_id] : [],
    ingredient: Array.isArray(values.ingredient)
      ? values.ingredient
      : values.ingredient ? [values.ingredient] : (ingredientData?.material_name ? [ingredientData.material_name] : []),
    unit: values.unit || ingredientData?.unit || null,
    parameter_id: values.parameter_id || null,
    parameter: values.parameter || parameterData?.parameter_name || null,
    parameter_unit: values.parameter_unit || parameterData?.unit || null,
    min_value: values.min_value ?? null,
    max_value: values.max_value ?? null,
    deviation_rule: values.deviation_rule || 'none',
    // Rule-based auto-fill depends on Movement Type:
    //   Charging / Dispensing / Transfer → To Machine auto-filled
    //   Discharge                        → From Machine auto-filled
    //   otherwise                        → no auto-fill
    from_machine_id: values.from_machine_id
      || (one_machine_rule_enabled &&
        initialMovementKey === 'discharge'
        ? setupMachineId
        : null)
      || null,
    from_machine_group_id: values.from_machine_group_id
      || (one_machine_rule_enabled &&
        initialMovementKey === 'discharge'
        ? setupEntry?.machine_group_id
        : null)
      || null,
    from_machine_category_id: values.from_machine_category_id
      || values.from_machine_id
      || (one_machine_rule_enabled &&
        initialMovementKey === 'discharge'
        ? setupEntry?.machine_type_id
        : null)
      || null,
    from_machine_name: values.from_machine_name
      || (one_machine_rule_enabled &&
        initialMovementKey === 'discharge'
        ? setupEntry?.machine_type
        : null)
      || null,
    from_machine_ids: Array.isArray(values.from_machine_ids) && values.from_machine_ids.length
      ? values.from_machine_ids
      : values.from_machine_ids_csv
        ? String(values.from_machine_ids_csv).split(',').map(Number).filter(Boolean)
        : (values.from_machine_id ? [values.from_machine_id] : (
          one_machine_rule_enabled &&
            initialMovementKey === 'discharge'
            ? setupMachineIds
            : []
        )),
    from_machine_ids_csv: values.from_machine_ids_csv || (
      Array.isArray(values.from_machine_ids) && values.from_machine_ids.length
        ? values.from_machine_ids.join(',')
        : (values.from_machine_id ? String(values.from_machine_id) : (
          one_machine_rule_enabled &&
            initialMovementKey === 'discharge'
            ? setupMachineIds.join(',')
            : ''
        ))
    ),
    to_machine_id: values.to_machine_id
      || (one_machine_rule_enabled &&
        ['charging', 'dispensing'].includes(initialMovementKey)
        ? setupMachineId
        : null)
      || null,
    to_machine_group_id: values.to_machine_group_id
      || (one_machine_rule_enabled &&
        ['charging', 'dispensing'].includes(initialMovementKey)
        ? setupEntry?.machine_group_id
        : null)
      || null,
    to_machine_category_id: values.to_machine_category_id
      || values.to_machine_id
      || (one_machine_rule_enabled &&
        ['charging', 'dispensing'].includes(initialMovementKey)
        ? setupEntry?.machine_type_id
        : null)
      || null,
    to_machine_name: values.to_machine_name
      || (one_machine_rule_enabled &&
        ['charging', 'dispensing'].includes(initialMovementKey)
        ? setupEntry?.machine_type
        : null)
      || null,
    to_machine_ids: Array.isArray(values.to_machine_ids) && values.to_machine_ids.length
      ? values.to_machine_ids
      : values.to_machine_ids_csv
        ? String(values.to_machine_ids_csv).split(',').map(Number).filter(Boolean)
        : (values.to_machine_id ? [values.to_machine_id] : (
          one_machine_rule_enabled &&
            ['charging', 'dispensing'].includes(initialMovementKey)
            ? setupMachineIds
            : []
        )),
    to_machine_ids_csv: values.to_machine_ids_csv || (
      Array.isArray(values.to_machine_ids) && values.to_machine_ids.length
        ? values.to_machine_ids.join(',')
        : (values.to_machine_id ? String(values.to_machine_id) : (
          one_machine_rule_enabled &&
            ['charging', 'dispensing'].includes(initialMovementKey)
            ? setupMachineIds.join(',')
            : ''
        ))
    ),
    execution_mode: lockedRuleFields?.execution_mode || values.execution_mode || 'Manual',
    verification_mode:
      lockedRuleFields?.verification_mode || values.verification_mode || 'QR Verification',
    multi_lot_strategy:
      lockedRuleFields?.multi_lot_strategy || values.multi_lot_strategy || 'Multi Lot Allowed',
    instruction: lockedRuleFields?.instruction ?? values.instruction ?? null,
    is_rm_code: editIndex !== null ? (values.is_rm_code ?? false) : (appSettings?.find((s) => s?.module === 'QR Code Verification')?.status === true ? true : false),
    is_quantity: editIndex !== null ? (values.is_quantity ?? false) : (appSettings?.find((s) => s?.module === 'QR Code Verification')?.status === true ? true : false),
    // Unpack stored `carry_over` nested object back into flat form fields on edit
    carry_over_type:
      values.carry_over_type ?? values.carry_over?.type ?? null,
    carry_over_prev_operation_ids:
      values.carry_over_prev_operation_ids ?? values.carry_over?.prev_operation_ids ?? [],
    carry_over_ref_ingredient_ids:
      values.carry_over_ref_ingredient_ids ?? values.carry_over?.ref_ingredient_ids ?? [],
    alias_name: values.alias_name || null,
    // BOM Rule Type — optional, drives server-side auto-calculation of
    // quantity (overrides Target Quantity above when set). All nullable.
    rule_type: values.rule_type || null,
    rule_value: values.rule_value ?? null,
    rule_uom_id: values.rule_uom_id || null,
    recipe_variant_id: values.recipe_variant_id || null,
    correction_formula_id: values.correction_formula_id || null,
    target_parameter_id: values.target_parameter_id || null,
    // BOM split — mutually exclusive with the rule fields above (see the
    // "Link to BOM Line" selector): this ingredient takes a share of a
    // recipe-level BOM line's total instead of carrying its own rule.
    recipe_bom_item_id: values.recipe_bom_item_id || null,
    bom_split_percent: values.bom_split_percent ?? null,
  };

  return (
    <>
      <style>{`
        @keyframes param-btn-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59,130,246,0.5); }
          50% { transform: scale(1.15); box-shadow: 0 0 0 5px rgba(59,130,246,0); }
        }
        @keyframes param-btn-pulse-remove {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
          50% { transform: scale(1.15); box-shadow: 0 0 0 5px rgba(239,68,68,0); }
        }
        .param-btn-add {
          animation: param-btn-pulse 1.6s ease-in-out infinite;
        }
        .param-btn-remove {
          animation: param-btn-pulse-remove 1.6s ease-in-out infinite;
        }
      `}</style>
      <Formik
        initialValues={initialValues}
        validationSchema={buildValidationSchema(ingredientLabel)}
        onSubmit={(values, { resetForm }) => {
          // Hard block: entered quantity must not exceed the remaining — mode-driven.
          //   Input-based recipe → limit ONLY the configured ingredient (others unrestricted).
          //   Master-batch recipe → limit EVERY ingredient against the master remaining.
          if (!values.carry_over_type) {
            const enteredQty = parseFloat(values.quantity) || 0;
            const selIds = Array.isArray(values.ingredient_id) ? values.ingredient_id : [];
            const isConfiguredIngredient =
              isInputBasedRecipe &&
              configuredIngredientId != null &&
              selIds.map(String).includes(String(configuredIngredientId));

            if (isInputBasedRecipe) {
              // Single-counter model: limit = committedConfigured + this row's original qty
              // (re-allocated). The backend enforces the hard recipe-wide cap on save.
              const cfgLimit =
                committedConfigured !== null
                  ? committedConfigured + (parseFloat(editingRowConfiguredOriginal) || 0)
                  : Infinity;
              if (
                isConfiguredIngredient &&
                ingredientRemainingInfo &&
                values.ingredient_id?.length === 1 &&
                enteredQty > cfgLimit
              ) {
                toast.error(
                  `${ingredientLabel} quantity is out of remaining. Only ${parseFloat(cfgLimit).toFixed(4)} ${ingredientRemainingInfo.unit || values.unit || ''} remaining for this ${ingredientLabel.toLowerCase()}.`
                );
                return;
              }
            } else if (committedMaster !== null) {
              // Master-batch recipe: every ingredient limited against the master remaining.
              const masterLimit = committedMaster + (parseFloat(editingRowMasterOriginal) || 0);
              if (enteredQty > masterLimit) {
                toast.error(
                  `${ingredientLabel} quantity is out of remaining. Only ${parseFloat(masterLimit).toFixed(4)} ${values.unit || ''} remaining in the batch.`
                );
                return;
              }
            }
          }

          // Structure the register data as expected by the API
          // Keep full register data (including names) for display in IngredientCard
          const registerData =
            values.machine_registers && values.machine_registers.length > 0
              ? values.machine_registers.map((machineReg) => ({
                machine_id: machineReg.machine_id,
                machine_name: machineReg.machine_name,
                register_ids: machineReg.register_ids, // IDs for API backend
                registers: machineReg.registers, // Full objects with names for display
              }))
              : null;

          const isWIPM = values.material_type === 'WIPM';
          // Product (on input-based Discharge) uses the same manual-name handling as WIPM.
          const isManualName = values.material_type === 'WIPM' || values.material_type === 'Product';
          // Strip raw carry-over form fields; only the structured `carry_over` object goes to backend
          const {
            carry_over_type: _coType,
            ...restValues
          } = values;
          // Auto-generate ingredient_code on Add (skip for Edit; keep existing if already set).
          // Find the highest existing I<NN> sequence and increment by 1, then ensure uniqueness.
          const isEditing = editIndex !== null && editIndex !== undefined;
          let computed_ingredient_code = values.ingredient_code || null;
          if (!isEditing && !computed_ingredient_code && process_code && operation_type_code) {
            // Gather all codes currently known in this operation — from parent props and locally added
            const existingSet = new Set(
              [
                ...(current_ingredients || []).map((i) => i.ingredient_code),
                ...locallyAddedIngredients.map((i) => i.ingredient_code),
              ]
                .filter(Boolean)
                .map(String)
            );
            const prefix = `${process_code}_${operation_type_code}_I`;
            let maxSeq = 0;
            existingSet.forEach((code) => {
              if (code.startsWith(prefix)) {
                const tail = code.slice(prefix.length);
                const num = parseInt(tail, 10);
                if (!Number.isNaN(num) && num > maxSeq) maxSeq = num;
              }
            });
            let next = maxSeq + 1;
            let candidate = `${prefix}${String(next).padStart(2, '0')}`;
            while (existingSet.has(candidate)) {
              next += 1;
              candidate = `${prefix}${String(next).padStart(2, '0')}`;
            }
            computed_ingredient_code = candidate;
          }
          console.log('Computed Ingredient Code:', computed_ingredient_code);

          // Resolve compliance_rules — new schema. Per-key default from rule,
          // overridden by any per-ingredient operator choice stored in values.compliance_rules.
          const _ruleBool = (key, fallback) =>
            lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
              ? !!lockedRuleFields[key]
              : fallback;
          const cr = values.compliance_rules || {};
          const _pick = (key, def) => (cr[key] !== undefined && cr[key] !== null ? !!cr[key] : _ruleBool(key, def));
          // Execution Mode caps which methods can be true, regardless of rule/override —
          // mirrors IngredientsRules.js's METHOD_AVAILABILITY (Automatic disallows QR/Barcode).
          const submitMethodAvailability = METHOD_AVAILABILITY[values.execution_mode] || METHOD_AVAILABILITY.Manual;
          const resolvedComplianceRules = {
            // Verification methods
            qr_scan: submitMethodAvailability.qr_scan && _pick('qr_scan', false),
            barcode: submitMethodAvailability.barcode && _pick('barcode', false),
            scale_weight: _pick('scale_weight', values.execution_mode === 'Automatic'),
            // Compliance checks
            verify_material_code: _pick('verify_material_code', true),
            verify_qty_in_range: _pick('verify_qty_in_range', true),
            photo_capture: _pick('photo_capture', false),
            kitting_required: _pick('kitting_required', false),
            double_verification: _pick('double_verification', false),
            material_reconciliation: _pick('material_reconciliation', false),
            critical_rm: _pick('critical_rm', false),
          };

          // Resolve execution_policy — new charging strategy schema.
          // 2026-09-02, "what we will be saving now? ... use those right side rules part" —
          // fill_strategy (max_fill/uniform) previously wasn't saved anywhere at all (it was
          // purely a live parameter to the capacity-check preview call); it now rides inside
          // this same JSON blob alongside the other Charging Strategy fields instead of a new
          // migrated column. null stays null (not forced to a default) — that's the real
          // "use the plant default" state, distinct from an explicit max_fill/uniform choice.
          const resolvedExecutionPolicy = {
            charging_mode_qr: values.charging_mode_qr || lockedRuleFields?.charging_mode_qr || 'Single QR',
            charging_mode_scale: values.charging_mode_scale || lockedRuleFields?.charging_mode_scale || 'Single Stretch',
            lot_strategy: values.lot_strategy || lockedRuleFields?.lot_strategy || 'Multi Lot',
            fill_strategy: values.fill_strategy ?? lockedRuleFields?.fill_strategy ?? null,
          };
          const resolvedFromMachineIds = Array.isArray(values.from_machine_ids) && values.from_machine_ids.length
            ? values.from_machine_ids
            : values.from_machine_ids_csv
              ? String(values.from_machine_ids_csv).split(',').map(Number).filter(Boolean)
              : (values.from_machine_id ? [values.from_machine_id] : []);
          const resolvedToMachineIds = Array.isArray(values.to_machine_ids) && values.to_machine_ids.length
            ? values.to_machine_ids
            : values.to_machine_ids_csv
              ? String(values.to_machine_ids_csv).split(',').map(Number).filter(Boolean)
              : (values.to_machine_id ? [values.to_machine_id] : []);

          const submittedValues = {
            ...restValues,
            compliance_rules: resolvedComplianceRules,
            execution_policy: resolvedExecutionPolicy,
            ingredient_code: computed_ingredient_code,
            machine_registers: registerData,
            from_machine_id: values.from_machine_id || null,
            from_machine_ids: resolvedFromMachineIds,
            from_machine_ids_csv: resolvedFromMachineIds.join(','),
            from_machine_category_id: values.from_machine_category_id || null,
            from_machine_group_id: values.from_machine_group_id || null,
            from_machine_name: values.from_machine_name || null,
            to_machine_id: values.to_machine_id || null,
            to_machine_ids: resolvedToMachineIds,
            to_machine_ids_csv: resolvedToMachineIds.join(','),
            to_machine_category_id: values.to_machine_category_id || null,
            to_machine_group_id: values.to_machine_group_id || null,
            to_machine_name: values.to_machine_name || null,
            // Manual-name types (WIPM, Product): clear RM-specific fields, keep manual name.
            ingredient_id: isManualName ? null : values.ingredient_id,
            ingredient: isManualName ? (values.ingredient_name_manual || null) : values.ingredient,
            quantity: isManualName ? (values.quantity_text ? null : (values.quantity ?? null)) : values.quantity,
            deviation: isManualName ? 0 : values.deviation,
            // API keys
            sub_operation_type: values.movement_type || null,
            material_type: values.material_type || null,
            deviation_rule: values.deviation_rule || 'none',
            is_work_in_progress_material: isWIPM,
            is_work_in_progress_quantity: isWIPM ? !!values.quantity_text : false,
            work_in_progress_material_name: isManualName ? (values.ingredient_name_manual || '') : '',
            is_qr_scan: resolvedComplianceRules.qr_scan,
            is_kitting: resolvedComplianceRules.kitting_required,
            is_rm_code: resolvedComplianceRules.verify_material_code,
            is_quantity: resolvedComplianceRules.verify_qty_in_range,
            // Critical RM flag — sent per-ingredient (also kept inside compliance_rules).
            critical_rm: resolvedComplianceRules.critical_rm,
            is_critical_rm: resolvedComplianceRules.critical_rm,
            // Scale machine config — 2026-09-02, "make it for group basis and multi
            // selection of machine not one, in reality we can use multiple and
            // runtime we use only one" — scale_machine_ids (plural, mirrors
            // to_machine_ids), not one drilled-down machine.
            scale_machine_type_id: values.scale_machine_type_id || null,
            scale_machine_group_id: values.scale_machine_group_id || null,
            scale_machine_ids: values.scale_machine_ids || [],
            scale_process_controls: values.scale_process_controls || [],
            carry_over: values.carry_over_type
              ? (() => {
                const refIds =
                  values.carry_over_type === 'current'
                    ? (
                      values.carry_over_ref_ingredient_ids || []
                    )
                    : [];
                const combined = [...(current_ingredients || []), ...locallyAddedIngredients];
                const ingredient_codes = combined
                  .filter((i) => refIds.map(String).includes(String(i.ingredient_id)))
                  .map((i) => i.ingredient_code)
                  .filter(Boolean);
                return {
                  type: values.carry_over_type,
                  prev_operation_ids:
                    values.carry_over_type === 'previous'
                      ? (
                        values.carry_over_prev_operation_ids || []
                      )
                      : [],
                  ref_ingredient_ids: refIds,
                  ingredient_codes,
                };
              })()
              : null,
          };

          // Track submitted ingredient locally so "Carry Forward → Current Operation"
          // can reference previously-added ingredients even if parent doesn't supply them.
          const addedId = submittedValues.ingredient_id || submittedValues.ingredient_name_manual;
          const addedName =
            submittedValues.ingredient ||
            submittedValues.work_in_progress_material_name ||
            submittedValues.ingredient_name_manual;
          if (addedId && addedName && (editIndex === null || editIndex === undefined)) {
            setLocallyAddedIngredients((prev) => {
              if (prev.some((p) => p.ingredient_id === addedId)) return prev;
              return [
                ...prev,
                {
                  ingredient_id: addedId,
                  ingredient: addedName,
                  ingredient_code: submittedValues.ingredient_code || null,
                  from_machine_id: submittedValues.from_machine_id || null,
                  from_machine_category_id: submittedValues.from_machine_category_id || null,
                  from_machine_group_id: submittedValues.from_machine_group_id || null,
                  from_machine_name: submittedValues.from_machine_name || null,
                  to_machine_id: submittedValues.to_machine_id || null,
                  to_machine_category_id: submittedValues.to_machine_category_id || null,
                  to_machine_group_id: submittedValues.to_machine_group_id || null,
                  to_machine_name: submittedValues.to_machine_name || null,
                },
              ];
            });
          }

          // eslint-disable-next-line no-console
          console.log('[Ingredient submit]', {
            raw_values: {
              from_machine_id: values.from_machine_id,
              from_machine_category_id: values.from_machine_category_id,
              from_machine_group_id: values.from_machine_group_id,
              from_machine_name: values.from_machine_name,
              to_machine_id: values.to_machine_id,
              to_machine_category_id: values.to_machine_category_id,
              to_machine_group_id: values.to_machine_group_id,
              to_machine_name: values.to_machine_name,
              movement_type: values.movement_type,
            },
            submittedValues,
          });
          onSubmit(submittedValues);
          resetForm();
        }}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, handleSubmit }) => {
          // Carry Over modal state — staged values (only committed to form on OK)
          /* eslint-disable react-hooks/rules-of-hooks */
          const [carryOverModalOpen, setCarryOverModalOpen] = useState(false);
          const [stagedCarryType, setStagedCarryType] = useState(null);
          const [stagedPrevOp, setStagedPrevOp] = useState([]);
          const [stagedRefIng, setStagedRefIng] = useState([]);
          const [transferAnchor, setTransferAnchor] = useState(null);
          const movementType = values.movement_type || values.sub_operation_type;
          const movementKey = normalizeMovementKey(movementType);
          const isTransfer = movementKey === 'transfer';
          const sourceLockedByRule = !!one_machine_rule_enabled && (
            movementKey === 'discharge' ||
            (isTransfer && transferAnchor === 'to')
          );
          const destinationLockedByRule = !!one_machine_rule_enabled && (
            ['charging', 'cleaning'].includes(movementKey) ||
            (isTransfer && transferAnchor === 'from')
          );
          const sourceLockedByCarryOver = !!(
            values.carry_over_type === 'current' &&
            values.carry_over_ref_ingredient_ids
          );
          const clearMachineSide = (side) => {
            const prefix = side === 'from' ? 'from_machine' : 'to_machine';
            if (side === 'from') {
              setFromMachineType(null);
              setFromMachineIds([]);
            } else {
              setToMachineType(null);
              setToMachineIds([]);
            }
            setFieldValue(`${prefix}_id`, null);
            setFieldValue(`${prefix}_ids`, []);
            setFieldValue(`${prefix}_ids_csv`, '');
            setFieldValue(`${prefix}_group_id`, null);
            setFieldValue(`${prefix}_category_id`, null);
            setFieldValue(`${prefix}_name`, null);
          };
          // Does this row's quantity fit the destination machine(s)' Usable
          // Capacity? Called from the quantity/ingredient/destination-machine
          // onChange handlers with whatever field just changed as an
          // override (their own setFieldValue call hasn't landed in `values`
          // yet at the point they call this, since React batches state
          // updates — so the field the user just touched has to be passed
          // explicitly rather than read back off `values`).
          const runCapacityCheck = (overrides = {}) => {
            const ingredientId = overrides.ingredientId !== undefined ? overrides.ingredientId : values.ingredient_id?.[0];
            const quantity = overrides.quantity !== undefined ? overrides.quantity : values.quantity;
            const unitId = overrides.unitId !== undefined ? overrides.unitId : values.unit_id;
            const machineIds = overrides.machineIds !== undefined ? overrides.machineIds : toMachineIds;
            const numberOfLots = overrides.numberOfLots !== undefined ? overrides.numberOfLots : values.number_of_lots;
            const fillStrategyOverride = overrides.fillStrategy !== undefined ? overrides.fillStrategy : values.fill_strategy;
            const fillStrategy = fillStrategyOverride || (uniformLotSizingDefault ? 'uniform' : 'max_fill');
            if (!ingredientId || !quantity || !unitId || !machineIds?.length) {
              setCapacityCheck(null);
              return;
            }
            setCapacityCheckLoading(true);
            check_material_machine_capacity(
              {
                material_inventory_id: ingredientId,
                quantity,
                unit_id: unitId,
                machine_ids: machineIds,
                number_of_lots: numberOfLots || undefined,
                fill_strategy: fillStrategy,
              },
              (res) => {
                setCapacityCheckLoading(false);
                if (res?.status === 'failed') {
                  setCapacityCheck(null);
                  return;
                }
                const result = res?.data || null;
                setCapacityCheck(result);
                // First time this row is found to not fit: default Number
                // of Lots to the minimum that would, so the field never
                // starts empty/misleading. Only when nothing's been set —
                // never overwrites a value the user already chose.
                if (result?.fits === false && !values.number_of_lots && result.minimum_lots_needed) {
                  setFieldValue('number_of_lots', result.minimum_lots_needed);
                }
              }
            );
          };
          // 2026-09-02, "it shoudl eb cliakble so on cleick calclaution show bottom and
          // right and should be able to claick all of them, there is [no] dropping ot
          // primary ot deconday" — every chip is equal and always clickable. Clicking
          // one re-runs the capacity check scoped to ONLY that machine (a scenario
          // preview — how much, how many lots, through THIS one), which updates both
          // the bottom alert and the Lot Distribution panel since they both read off
          // the same capacityCheck. to_machine_ids/the real Destination Equipment
          // selection is never touched, so this has no effect on what gets saved.
          const previewMachineDistribution = (machine) => {
            setScrollTargetMachineId(machine.machine_id);
            runCapacityCheck({ machineIds: [machine.machine_id] });
          };
          const applySetupMachineSide = (side) => {
            const setup = machine_setup_entries?.[0];
            if (!setup?.machine_type_id) return;
            const activeSetupMachineIds = setupMachineIds;
            const activeSetupMachineId = activeSetupMachineIds[0] || null;
            const setupCat = machineTypeWithGroups.find(
              (c) => String(c.machine_category_id) === String(setup.machine_type_id)
            );
            const setupGrp = setupCat?.machine_groups?.find(
              (g) => String(g.machine_group_id) === String(setup.machine_group_id)
            );
            const groupMachineIds = (setupGrp?.machines || []).map((m) => Number(m.machine_id));
            const setupLeaf = buildCascaderLeafValue(activeSetupMachineIds, groupMachineIds);
            const setupPath = setup.machine_type_id && setup.machine_group_id
              ? [setup.machine_type_id, setup.machine_group_id, setupLeaf]
              : null;
            const prefix = side === 'from' ? 'from_machine' : 'to_machine';
            if (side === 'from') {
              setFromMachineType(setupPath);
              setFromMachineIds(activeSetupMachineIds);
            } else {
              setToMachineType(setupPath);
              setToMachineIds(activeSetupMachineIds);
            }
            setFieldValue(`${prefix}_id`, activeSetupMachineId);
            setFieldValue(`${prefix}_ids`, activeSetupMachineIds);
            setFieldValue(`${prefix}_ids_csv`, activeSetupMachineIds.join(','));
            setFieldValue(`${prefix}_category_id`, setup.machine_type_id);
            setFieldValue(`${prefix}_group_id`, setup.machine_group_id || null);
            setFieldValue(`${prefix}_name`, setup.machine_type || null);
          };
          const resetTransferMachines = () => {
            setTransferAnchor(null);
            clearMachineSide('from');
            clearMachineSide('to');
          };
          const flattenMachineRoutes = (routeCats) =>
            (routeCats || []).flatMap((cat) =>
              (cat.machine_groups || []).map((group) => ({
                cat,
                group,
                machineIds: parseMachineIdsList(
                  group.machine_ids,
                  group.machine_ids_csv,
                  group.machine_id
                ),
              }))
            );
          const applyMachineRouteSide = (side, route) => {
            if (!route?.cat || !route?.group) return;
            const selectedIds = route.machineIds.length
              ? route.machineIds
              : (route.group.machines || []).map((m) => Number(m.machine_id)).filter(Boolean);
            const groupMachineIds = (route.group.machines || [])
              .map((m) => Number(m.machine_id))
              .filter(Boolean);
            const leaf = buildCascaderLeafValue(selectedIds, groupMachineIds);
            const path = [route.cat.machine_category_id, route.group.machine_group_id, leaf];
            const label = `${route.cat.machine_category_name || ''} / ${route.group.machine_group_name || ''}`;
            const prefix = side === 'from' ? 'from_machine' : 'to_machine';
            if (side === 'from') {
              setFromMachineType(path);
              setFromMachineIds(selectedIds);
            } else {
              setToMachineType(path);
              setToMachineIds(selectedIds);
            }
            setFieldValue(`${prefix}_id`, selectedIds[0] || null);
            setFieldValue(`${prefix}_ids`, selectedIds);
            setFieldValue(`${prefix}_ids_csv`, selectedIds.join(','));
            setFieldValue(`${prefix}_category_id`, route.cat.machine_category_id || null);
            setFieldValue(`${prefix}_group_id`, route.group.machine_group_id || null);
            setFieldValue(`${prefix}_name`, label);
          };

          // Restore transfer anchor when editing a saved Transfer ingredient.
          useEffect(() => {
            if (editIndex === null || editIndex === undefined) return;
            if (!isTransfer || !one_machine_rule_enabled) return;
            const inferred = inferTransferAnchor(
              parseMachineIdsList(values.from_machine_ids, values.from_machine_ids_csv, values.from_machine_id),
              parseMachineIdsList(values.to_machine_ids, values.to_machine_ids_csv, values.to_machine_id),
              setupMachineIds
            );
            if (inferred) setTransferAnchor(inferred);
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [editIndex, machineTypeWithGroups.length, setupMachineIds.join(',')]);

          // Auto-fill the LOCKED machine from Machine Setup — runs only when movement type changes.
          // The opposite field is reset when switching movement type so stale locks
          // do not remain from the previous direction.
          useEffect(() => {
            if (!one_machine_rule_enabled) return;
            const setup = machine_setup_entries?.[0];
            if (!setup?.machine_type_id) return;
            if (isTransfer) return;
            if (movementKey === 'discharge') {
              setTransferAnchor(null);
              applySetupMachineSide('from');
              clearMachineSide('to');
            } else if (['charging', 'dispensing'].includes(movementKey)) {
              setTransferAnchor(null);
              applySetupMachineSide('to');
              clearMachineSide('from');
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [
            movementKey,
            one_machine_rule_enabled,
            machine_setup_entries,
          ]);

          // Fetch upstream/dependency machines for Carry Over (previous op) or Transfer routing.
          useEffect(() => {
            if (!recipe_id) {
              setPrevOpFromMachines([]);
              return;
            }
            let operationNumber = null;
            if (
              values.carry_over_type === 'previous' &&
              values.carry_over_prev_operation_ids?.length
            ) {
              operationNumber = (values.carry_over_prev_operation_ids || [])[0];
            } else if (isTransfer && (previous_operations || []).length) {
              operationNumber = previous_operations[0].operation_number;
            }
            if (!operationNumber) {
              setPrevOpFromMachines([]);
              return;
            }
            get_ingredient_from_machine_categories_by_op(
              {
                operation_number: operationNumber,
                recipe_id,
              },
              (res) => {
                if (res?.data?.status === 'success') {
                  setPrevOpFromMachines(res?.data?.data || []);
                } else {
                  setPrevOpFromMachines([]);
                }
              }
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [
            values.carry_over_type,
            values.carry_over_prev_operation_ids,
            recipe_id,
            isTransfer,
            previous_operations,
          ]);

          useEffect(() => {
            if (
              !carryOverModalOpen ||
              stagedCarryType !== 'previous' ||
              !stagedPrevOp?.length ||
              !recipe_id
            ) {
              return;
            }
            get_ingredient_from_machine_categories_by_op(
              {
                operation_number: stagedPrevOp[0],
                recipe_id,
              },
              (res) => {
                if (res?.data?.status === 'success') {
                  setPrevOpFromMachines(res?.data?.data || []);
                } else {
                  setPrevOpFromMachines([]);
                }
              }
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [carryOverModalOpen, stagedCarryType, stagedPrevOp, recipe_id]);
          /* eslint-enable react-hooks/rules-of-hooks */

          const useUpstreamMachineOptions =
            (values.carry_over_type === 'previous' && values.carry_over_prev_operation_ids?.length) ||
            (isTransfer && (previous_operations || []).length && prevOpFromMachines.length > 0);
          const upstreamMachineOptions = useUpstreamMachineOptions
            ? prevOpFromMachines
            : machineTypeWithGroups;
          const sourceCascaderOptions = buildMachineCascaderOptions(
            !sourceLockedByRule ? upstreamMachineOptions : machineTypeWithGroups
          );
          const destinationCascaderOptions = buildMachineCascaderOptions(
            !destinationLockedByRule && isTransfer && (previous_operations || []).length
              ? upstreamMachineOptions
              : machineTypeWithGroups
          );
          const upstreamSourceOperationNumbers = [
            ...new Set(
              flattenMachineRoutes(prevOpFromMachines)
                .map((route) => route.group?.source_operation_number)
                .filter(Boolean)
            ),
          ];

          const openCarryOverModal = () => {
            setStagedCarryType(values.carry_over_type || null);
            // Auto-pick the dependency if exactly one operation is available
            const autoPrevOp = values.carry_over_prev_operation_ids?.length
              ? values.carry_over_prev_operation_ids
              : (((previous_operations || []).length === 1
                ? [previous_operations[0].operation_number]
                : []));
            setStagedPrevOp(autoPrevOp);
            setStagedRefIng(
              values.carry_over_ref_ingredient_ids || []
            );
            setCarryOverModalOpen(true);
          };
          const confirmCarryOver = () => {
            setFieldValue('carry_over_type', stagedCarryType || null);
            setFieldValue(
              'carry_over_prev_operation_ids',
              stagedCarryType === 'previous' ? (stagedPrevOp || []) : []
            );
            setFieldValue(
              'carry_over_ref_ingredient_ids',
              stagedCarryType === 'current' ? (stagedRefIng || []) : []
            );
            // Auto-fill From Machine from the referenced ingredient's To Machine (Carry Over)
            if (stagedCarryType === 'current' && stagedRefIng?.length) {
              const firstRef = stagedRefIng[0];
              const refIng =
                (current_ingredients || []).find(
                  (i) => String(i.ingredient_id) === String(firstRef)
                ) ||
                locallyAddedIngredients.find(
                  (i) => String(i.ingredient_id) === String(firstRef)
                );
              if (refIng?.to_machine_id || refIng?.to_machine_ids?.length || refIng?.to_machine_ids_csv) {
                const refFromIds = parseMachineIdsList(
                  refIng.to_machine_ids,
                  refIng.to_machine_ids_csv,
                  refIng.to_machine_id
                );
                const refMachineId = refFromIds[0] || refIng.to_machine_id || null;
                setFieldValue('from_machine_id', refMachineId);
                setFieldValue('from_machine_ids', refFromIds);
                setFieldValue('from_machine_ids_csv', refFromIds.join(','));
                setFieldValue(
                  'from_machine_category_id',
                  refIng.to_machine_category_id || refMachineId
                );
                setFieldValue('from_machine_group_id', refIng.to_machine_group_id || null);
                setFieldValue('from_machine_name', refIng.to_machine_name || null);
                setFromMachineIds(refFromIds);
                if (machineTypeWithGroups.length && refIng.to_machine_category_id && refIng.to_machine_group_id) {
                  const cat = machineTypeWithGroups.find(
                    (c) => String(c.machine_category_id) === String(refIng.to_machine_category_id)
                  );
                  const grp = cat?.machine_groups?.find(
                    (g) => String(g.machine_group_id) === String(refIng.to_machine_group_id)
                  );
                  const groupMachineIds = (grp?.machines || []).map((m) => Number(m.machine_id));
                  const leaf = buildCascaderLeafValue(refFromIds, groupMachineIds);
                  setFromMachineType([refIng.to_machine_category_id, refIng.to_machine_group_id, leaf]);
                }
              }
            } else if (stagedCarryType === 'previous' && stagedPrevOp?.length) {
              const routes = flattenMachineRoutes(prevOpFromMachines);
              if (routes.length === 1) {
                applyMachineRouteSide('from', routes[0]);
                if (isTransfer && one_machine_rule_enabled) {
                  setTransferAnchor('from');
                  applySetupMachineSide('to');
                }
              } else if (routes.length === 0) {
                clearMachineSide('from');
              }
            }
            setCarryOverModalOpen(false);
          };
          const clearCarryOver = () => {
            setFieldValue('carry_over_type', null);
            setFieldValue('carry_over_prev_operation_ids', []);
            setFieldValue('carry_over_ref_ingredient_ids', []);
            setStagedCarryType(null);
            setStagedPrevOp(null);
            setStagedRefIng(null);
          };
          const carryOverSummary = (() => {
            if (!values.carry_over_type) return null;
            if (values.carry_over_type === 'current') {
              const combined = [...(current_ingredients || []), ...locallyAddedIngredients];
              const ids = values.carry_over_ref_ingredient_ids || [];
              const names = ids.map((id) => {
                const ing = combined.find((i) => String(i.ingredient_id) === String(id));
                return ing?.ingredient || ing?.ingredient_name || id;
              });
              return `Current → ${names.join(', ') || '?'}`;
            }
            const prevIds = values.carry_over_prev_operation_ids || [];
            const names = prevIds.map((pid) => {
              const pobj = (previous_operations || []).find((o) => String(o.operation_number) === String(pid));
              const num = pobj?.operation_number ?? pid;
              const name = pobj?.operation_name || pobj?.name;
              return name ? `${name} (Op ${num})` : `Op ${num}`;
            });
            return `Previous → ${names.join(', ')}`;
          })();

          // ── Right-panel Runtime Compliance Rules — editable per ingredient unless
          //    the assigned Ingredient Rule locks the group.
          //    Default value comes from the Ingredient Rule (lockedRuleFields) or a
          //    derived fallback; the operator can override per item. The chosen
          //    values are stored on values.compliance_rules and saved in the payload.
          const qrEnabledNow = appSettings?.find((s) => s?.module === 'QR Code Verification')?.status === true;
          const ruleVal = (key, fallback) =>
            lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
              ? !!lockedRuleFields[key]
              : fallback;
          // Per-key default (rule value, else a sensible derived default).
          const complianceDefaults = {
            verify_material_code: ruleVal('verify_material_code', true),
            verify_qty_in_range: ruleVal('verify_qty_in_range', true),
            photo_capture: ruleVal('photo_capture', false),
            kitting_required: ruleVal('kitting_required', false),
            double_verification: ruleVal('double_verification', false),
            material_reconciliation: ruleVal('material_reconciliation', false),
            critical_rm: ruleVal('critical_rm', false),
          };
          // Effective value: explicit per-item choice if set, else the default.
          const complianceVal = (key) => {
            const cr = values.compliance_rules || {};
            return cr[key] !== undefined && cr[key] !== null ? !!cr[key] : !!complianceDefaults[key];
          };
          const toggleCompliance = (key) => {
            if (isComplianceRuleLocked(key)) return;
            const cr = { ...(values.compliance_rules || {}) };
            cr[key] = !complianceVal(key);
            setFieldValue('compliance_rules', cr);
          };

          // Verification method helpers — stored in compliance_rules for override
          const METHOD_KEYS = ['qr_scan', 'barcode', 'scale_weight'];
          const methodAvailability = METHOD_AVAILABILITY[values.execution_mode] || METHOD_AVAILABILITY.Manual;
          const isMethodAvailable = (key) => methodAvailability[key] !== false;
          const methodVal = (key) => {
            if (!isMethodAvailable(key)) return false;
            const cr = values.compliance_rules || {};
            const ruleDefault = lockedRuleFields?.[key] ?? false;
            return cr[key] !== undefined ? !!cr[key] : !!ruleDefault;
          };
          const toggleMethod = (key) => {
            if (isRuleFieldLocked(key) || !isMethodAvailable(key)) return;
            const cr = { ...(values.compliance_rules || {}) };
            cr[key] = !methodVal(key);
            setFieldValue('compliance_rules', cr);
          };
          const scaleOn = methodVal('scale_weight') || values.execution_mode === 'Automatic';
          // 2026-09-02, "make it for group basis and multi selection of machine not
          // one, in reality we can use multiple and runtime we use only one" — the
          // cascader only ever resolves Category -> Group now; which machine(s) in
          // that group are viable scales is the separate toggle list below.
          const scaleCascaderValue = values.scale_machine_type_id
            ? values.scale_machine_group_id
              ? [values.scale_machine_type_id, values.scale_machine_group_id]
              : [values.scale_machine_type_id]
            : undefined;
          const scaleGroupMachines = (() => {
            const cat = (machineTypeWithGroups || []).find((c) => String(c.machine_category_id) === String(values.scale_machine_type_id));
            const grp = cat?.machine_groups?.find((g) => String(g.machine_group_id) === String(values.scale_machine_group_id));
            return grp?.machines || [];
          })();
          const scaleMachineIds = values.scale_machine_ids || [];
          const handleScaleGroupChange = (val, opts) => {
            const leaf = opts?.[opts.length - 1];
            const catId = leaf?._catId ?? val?.[0] ?? null;
            const grpId = leaf?._grpId ?? val?.[1] ?? null;
            setFieldValue('scale_machine_type_id', catId);
            setFieldValue('scale_machine_group_id', grpId);
            setFieldValue('scale_machine_ids', []);
            setFieldValue('scale_process_controls', []);
            setScaleRegisters([]);
          };
          // 2026-09-02, "still same" — the register fetch was only ever wired to a
          // top-level useEffect keyed on the OUTER `values` prop (the incoming saved
          // record, for edit-prefill) — setFieldValue here updates Formik's live
          // internal state instead, a completely different object, so that effect
          // never re-fired on a live toggle click. Fetch directly here instead, using
          // the real up-to-date selection.
          const toggleScaleMachine = (machineId) => {
            const next = scaleMachineIds.includes(machineId)
              ? scaleMachineIds.filter((id) => id !== machineId)
              : [...scaleMachineIds, machineId];
            setFieldValue('scale_machine_ids', next);
            setFieldValue('scale_process_controls', []);
            const representative = next[0];
            if (!representative) { setScaleRegisters([]); return; }
            setScaleRegistersLoading(true);
            get_process_control_parameter(
              (res) => { setScaleRegisters(res?.data?.data || []); setScaleRegistersLoading(false); },
              null,
              representative,
            );
          };
          const complianceRules = [
            { key: 'verify_material_code', label: 'Material Identity', desc: `Scanned material code must match the expected ${ingredientLabel.toLowerCase()}` },
            { key: 'verify_qty_in_range', label: 'Quantity Tolerance', desc: 'Actual quantity must fall within the configured deviation range' },
            { key: 'photo_capture', label: 'Photo Capture', desc: 'Operator must take a photo as visual proof of the addition' },
            { key: 'kitting_required', label: 'Kitting Verified', desc: 'Material must be pre-kitted and verified before charging' },
            { key: 'double_verification', label: 'Double Verification', desc: 'A second operator must independently verify the addition' },
            { key: 'material_reconciliation', label: 'Material Reconciliation', desc: 'Inventory records must be updated and balanced after charging' },
          ]
            .filter((r) => lockedRuleFields?.[`${r.key}_hidden`] !== true)
            .map((r) => ({ ...r, yes: complianceVal(r.key) }));

          const _activeRuleMethods = {
            qr_scan: lockedRuleFields?.qr_scan || false,
            barcode: lockedRuleFields?.barcode || false,
            scale_weight: lockedRuleFields?.scale_weight || values.execution_mode === 'Automatic',
            photo_capture: lockedRuleFields?.photo_capture || false,
          };
          const executionFlowSteps = [
            _activeRuleMethods.qr_scan && 'Scan QR Code',
            _activeRuleMethods.barcode && 'Scan Barcode',
            _activeRuleMethods.scale_weight && (
              values.execution_mode === 'Automatic' ? 'Auto-fetch Weight from Scale' : 'Read & Enter Weight'
            ),
            _activeRuleMethods.photo_capture && 'Capture Photo',
            lockedRuleFields?.verify_material_code !== false && 'Verify Material Identity',
            (lockedRuleFields?.verify_qty_in_range !== false || values.execution_mode === 'Automatic') && 'Validate Quantity Tolerance',
            'Execute Material Addition',
            lockedRuleFields?.kitting_required && 'Verify Kitting',
          ].filter(Boolean);
          const sectionHeader = (text) => (
            <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mb-3">{text}</p>
          );

          // ── Input-based recipe (intermediate selected in metadata) special cases ──
          const movementKeyLower = (values.movement_type || '').toLowerCase();
          const isCharging = movementKeyLower === 'charging';
          const isDischarge = movementKeyLower === 'discharge';

          // RM tooltip option (reused).
          const rmOption = {
            value: 'RM',
            label: (
              <Tooltip title="Raw Material" placement="right"><span>RM</span></Tooltip>
            ),
          };
          const wipmOption = {
            value: 'WIPM',
            label: (
              <Tooltip title="Work In Progress Material" placement="right"><span>WIPM</span></Tooltip>
            ),
          };
          const productOption = {
            value: 'Product',
            label: (
              <Tooltip title="Product" placement="right"><span>Product</span></Tooltip>
            ),
          };
          const intermediateOption = {
            value: 'Intermediate',
            label: (
              <Tooltip title="Intermediate" placement="right"><span>Intermediate</span></Tooltip>
            ),
          };

          // Material Type options. Default = RM / WIPM. For input-based recipes on
          // Discharge: Product / WIPM / Intermediate — or WIPM-only when the operation
          // has no Product sub-operation.
          let materialTypeOptions = [rmOption, wipmOption];
          if (isInputBasedRecipe && isDischarge) {
            materialTypeOptions = hasProductSubop
              ? [productOption, wipmOption, intermediateOption]
              : [wipmOption];
          }

          // Ingredient dropdown options.
          //  • Default: all inventory (the fetch itself already scopes out packaging
          //    materials via module_type: 'prod' -> is_packaging=False server-side).
          //  • Charging (input-based): all Ingredients + the metadata intermediate.
          //  • Discharge + Intermediate material type: only the metadata intermediate.
          const baseIngredientOptions = (inventory_list || [])
            .map((i) => ({
              value: i.material_id,
              label: i.material_name,
              unit: i.unit,
              unit_id: i.unit_id,
              bulk_density: i.bulk_density,
              material_unique_id: i.material_unique_id,
            }));
          const intermediateInvItem = configuredIngredientId
            ? (inventory_list || []).find(
              (i) => String(i.material_id) === String(configuredIngredientId)
            )
            : null;
          const intermediateOptionItem = configuredIngredientId
            ? {
              value: configuredIngredientId,
              label: configuredIngredientName || intermediateInvItem?.material_name || 'Intermediate',
              unit: intermediateInvItem?.unit,
              unit_id: intermediateInvItem?.unit_id,
            }
            : null;

          let ingredientOptions = baseIngredientOptions;
          if (isInputBasedRecipe && isCharging && intermediateOptionItem) {
            // All ingredients + the metadata intermediate (avoid duplicate).
            ingredientOptions = baseIngredientOptions.some(
              (o) => String(o.value) === String(intermediateOptionItem.value)
            )
              ? baseIngredientOptions
              : [...baseIngredientOptions, intermediateOptionItem];
          } else if (isInputBasedRecipe && isDischarge && values.material_type === 'Intermediate') {
            // Only the metadata intermediate.
            ingredientOptions = intermediateOptionItem ? [intermediateOptionItem] : [];
          }

          // "Manual name" material types (no inventory ingredient picker): WIPM and Product.
          const isManualNameType =
            values.material_type === 'WIPM' || values.material_type === 'Product';
          const filteredBomItems = (recipeBomItemList || []).filter(
            (item) => normalizeBomMaterialType(item.material_type) === values.material_type
          );
          const activeBomItem = filteredBomItems.find((item) => item.bom_item_id === values.recipe_bom_item_id)
            || recipeBomItemList.find((item) => item.bom_item_id === values.recipe_bom_item_id);
          const bomStdQtyLimit = activeBomItem?.computed_quantity ?? null;
          const bomIngredientOptions = filteredBomItems.map((item) => {
            const inventoryMatch = (inventory_list || []).find(
              (inv) => String(inv.material_id) === String(item.material_inventory_id)
            );
            const materialCode = inventoryMatch?.material_unique_id || inventoryMatch?.code || '';
            const materialNameWithCode = [item.material_name, materialCode].filter(Boolean).join(' - ');
            const aliasSuffix =
              item.alias && item.alias !== item.material_name ? ` · ${item.alias}` : '';
            const lineLabel = `${materialNameWithCode}${aliasSuffix}`.trim();
            return {
              value: item.bom_item_id,
              label: lineLabel,
              searchLabel: `${item.material_name || ''} ${materialCode || ''} ${item.alias || ''} ${item.group_label || ''}`.trim(),
              materialInventoryId: item.material_inventory_id,
              materialLabel: item.material_name,
              // 2026-09-02, "i just select the material and machines are there still no
              // lots though why?" — this used to depend entirely on inventoryMatch (a
              // separate inventory_list fetch cross-referenced by material_inventory_id),
              // silently None whenever that other list didn't happen to include this
              // material. get_recipe_bom_item_list now returns the unit directly
              // (material_unit_id/material_unit_name) — prefer that; inventoryMatch is
              // just a fallback for older backends that don't send it yet.
              unit: item.material_unit_name || inventoryMatch?.unit || item.recipe_unit_name || null,
              unit_id: item.material_unit_id ?? inventoryMatch?.unit_id ?? null,
              defaultQuantity: item.computed_quantity ?? null,
              quantityStrategy: getQuantityStrategyFromRuleType(item.rule_type),
            };
          });
          const bomGroupMap = new Map();
          bomIngredientOptions.forEach((option) => {
            const sourceItem = filteredBomItems.find((item) => String(item.bom_item_id) === String(option.value));
            const groupLabel = sourceItem?.group_label || 'Ungrouped';
            const groupKey = sourceItem?.group_label || '__ungrouped__';
            if (!bomGroupMap.has(groupKey)) {
              bomGroupMap.set(groupKey, {
                value: groupKey,
                label: groupLabel,
                children: [],
              });
            }
            bomGroupMap.get(groupKey).children.push({
              value: option.value,
              key: option.value,
              title: option.label,
              label: option.label,
              bulletPrefix: '•',
              shortTitle: option.label,
              rawTitle: option.label,
              searchLabel: `${groupLabel} ${option.searchLabel}`.trim(),
            });
          });
          const bomTreeOptions = Array.from(bomGroupMap.values()).map((group) => ({
            ...group,
            key: group.value,
            title: group.label,
          }));
          const useBomFilteredSelector = !isManualNameType && filteredBomItems.length > 0;

          return (
            <Form layout="vertical" onFinish={handleSubmit}>
              {/* No fixed height / inner scroll here — the enclosing Modal (in
                  SubOperationDetails.js) is the single scroll container for the
                  whole popup (form + footer Save button + Required File Uploads
                  bar), so this just lays out naturally and lets the modal scroll
                  as one unit instead of nesting a second scrollbar in here. */}
              <div className="flex gap-4">
                {/* ===== LEFT: Main Form ===== */}
                <div className={`min-w-0 flex flex-col ${rulesEnabled ? 'w-2/3' : 'w-full'}`}>

                  <div style={{ paddingRight: 4 }}>

                    {/* ── Section: MATERIAL DEFINITION ── */}
                    <div
                      className="mb-4 rounded-lg border bg-white dark:bg-slate-900"
                      style={{ borderColor: '#e5e7eb', padding: '16px 18px' }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        {sectionHeader('Material Definition')}
                        {/* Display-only badges — not persisted */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {operation_is_critical && (
                            <Tag color="red" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                              CRITICAL MATERIAL
                            </Tag>
                          )}
                          {values.execution_mode === 'Manual' && (
                            <Tag color="blue" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                              MANUAL EXECUTION
                            </Tag>
                          )}
                          {(values.is_rm_code || qrEnabledNow) && (
                            <Tag color="green" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                              QR VERIFIED
                            </Tag>
                          )}
                          {/* Critical RM — moved inline right after QR VERIFIED. Hidden
                              when the rule marks it hidden; disabled when locked. */}
                          {lockedRuleFields?.critical_rm_hidden !== true && (
                            <span className="flex items-center gap-1.5" style={{ marginLeft: 4 }}>
                              <span className="flex items-center gap-1">
                                Critical RM (CMA)
                                {isComplianceRuleLocked('critical_rm') && <Lock className="w-3 h-3 text-yellow-600" />}
                              </span>
                              <Switch
                                size="small"
                                checked={!!complianceVal('critical_rm')}
                                disabled={isComplianceRuleLocked('critical_rm')}
                                onChange={() => toggleCompliance('critical_rm')}
                                checkedChildren="Yes"
                                unCheckedChildren="No"
                              />
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Row 1: Movement Type + Material Type + Ingredient */}
                      <Row gutter={16} className="mb-2">
                        <Col span={8}>
                          <Form.Item
                            label={
                              <span className="flex items-center gap-1">
                                Movement Type
                                {isRuleFieldLocked('movement_type') && (
                                  <Lock className="w-3 h-3 text-yellow-600" />
                                )}
                              </span>
                            }
                            validateStatus={touched.movement_type && errors.movement_type ? 'error' : ''}
                            help={touched.movement_type && errors.movement_type}
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              value={values.movement_type}
                              onChange={(val) => {
                                setFieldValue('movement_type', val || null);
                                setTransferAnchor(null);
                                clearMachineSide('from');
                                clearMachineSide('to');
                              }}
                              placeholder="Select Movement Type"
                              allowClear
                              disabled={isRuleFieldLocked('movement_type')}
                              options={[
                                { value: 'Charging', label: 'Charging' },
                                { value: 'Dispensing', label: 'Dispensing' },
                                { value: 'Transfer', label: 'Transfer' },
                                { value: 'Discharge', label: 'Discharge' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label={
                              <span className="flex items-center gap-1">
                                Material Type
                                {isRuleFieldLocked('material_type') && (
                                  <Lock className="w-3 h-3 text-yellow-600" />
                                )}
                              </span>
                            }
                            validateStatus={touched.material_type && errors.material_type ? 'error' : ''}
                            help={touched.material_type && errors.material_type}
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              value={values.material_type}
                              onChange={(val) => {
                                setFieldValue('material_type', val || null);
                                // WIPM and Product use a manual name (no inventory picker).
                                if (val === 'WIPM' || val === 'Product') {
                                  setFieldValue('deviation', 0);
                                  setFieldValue('quantity', null);
                                  setFieldValue('ingredient_id', null);
                                  setFieldValue('ingredient', null);
                                  setFieldValue('unit', null);
                                  setFieldValue('recipe_bom_item_id', null);
                                  setFieldValue('bom_split_percent', null);
                                } else {
                                  setFieldValue('ingredient_name_manual', null);
                                  setFieldValue('quantity_text', null);
                                  setFieldValue('ingredient_id', []);
                                  setFieldValue('ingredient', []);
                                  setFieldValue('recipe_bom_item_id', null);
                                  setFieldValue('bom_split_percent', null);
                                }
                                // Clear carry-over + machine fields when material type switches
                                setFieldValue('carry_over_type', null);
                                setFieldValue('carry_over_prev_operation_ids', []);
                                setTransferAnchor(null);
                                clearMachineSide('from');
                                clearMachineSide('to');
                              }}
                              placeholder="Select Material Type"
                              disabled={isRuleFieldLocked('material_type')}
                              options={materialTypeOptions}
                            />
                          </Form.Item>
                        </Col>
                        {!isManualNameType ? (
                          <Col span={8}>
                            <Form.Item
                              label={ingredientLabel}
                              validateStatus={touched.ingredient_id && errors.ingredient_id ? 'error' : ''}
                              help={touched.ingredient_id && errors.ingredient_id}
                              style={{ marginBottom: 0 }}
                            >
                              {useBomFilteredSelector ? (
                                <TreeSelect
                                  id="ingredient_id"
                                  className="ingredient-select w-full"
                                  value={values.recipe_bom_item_id || undefined}
                                  onChange={(selectedBomItemId) => {
                                    const selected = bomIngredientOptions.find((opt) => String(opt.value) === String(selectedBomItemId));
                                    setIngredientRemainingInfo(null);
                                    if (!selected) {
                                      setFieldValue('recipe_bom_item_id', null);
                                      setFieldValue('ingredient_id', []);
                                      setFieldValue('ingredient', []);
                                      setFieldValue('unit', null);
                                      setFieldValue('unit_id', null);
                                      setFieldValue('quantity', null);
                                      return;
                                    }
                                    setFieldValue('recipe_bom_item_id', selected.value);
                                    setFieldValue('bom_split_percent', null);
                                    setFieldValue('ingredient_id', [selected.materialInventoryId]);
                                    setFieldValue('ingredient', [selected.materialLabel]);
                                    setFieldValue('unit', selected.unit);
                                    // 2026-09-02, "i just select the material and machines are there
                                    // still no lots though why?" — this was the real cause: unit_id
                                    // (what runCapacityCheck actually reads) never got set from this
                                    // picker, only the display-name `unit` field did, so the capacity
                                    // check's `!unitId` guard silently bailed out every time a material
                                    // was picked here — nothing to do with machines or quantity at all.
                                    setFieldValue('unit_id', selected.unit_id);
                                    setFieldValue('alias_name', selected.materialLabel || null);
                                    setFieldValue('is_percentage', false);
                                    const resolvedQuantity =
                                      selected.defaultQuantity !== null && selected.defaultQuantity !== undefined
                                        ? selected.defaultQuantity
                                        : values.quantity;
                                    if (selected.defaultQuantity !== null && selected.defaultQuantity !== undefined) {
                                      setFieldValue('quantity', selected.defaultQuantity);
                                      if (onQuantityChange) onQuantityChange(selected.defaultQuantity, values.quantity, [selected.materialInventoryId]);
                                    }
                                    runCapacityCheck({
                                      ingredientId: selected.materialInventoryId,
                                      unitId: selected.unit_id,
                                      quantity: resolvedQuantity,
                                    });
                                    if (recipe_id) {
                                      get_recipe_ingredient_remaining(
                                        (res) => {
                                          if (res?.data?.status === 'success') {
                                            setIngredientRemainingInfo(res.data.data);
                                          }
                                        },
                                        { recipe_id, material_id: selected.materialInventoryId }
                                      );
                                    }
                                  }}
                                  treeData={bomTreeOptions}
                                  placeholder={`Select ${values.material_type} from BOM`}
                                  allowClear
                                  showSearch
                                  treeDefaultExpandAll
                                  treeNodeFilterProp="searchLabel"
                                  popupClassName="bom-tree-dropdown"
                                  dropdownStyle={{ maxHeight: 420, overflow: 'auto' }}
                                  treeTitleRender={(node) => (
                                    <span
                                      className="bom-tree-node-title"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        minWidth: 0,
                                      }}
                                      title={node.rawTitle || node.searchLabel || node.title || node.label}
                                    >
                                      {node.bulletPrefix ? (
                                        <span style={{ flex: '0 0 auto', color: '#64748b', fontSize: 12 }}>
                                          {node.bulletPrefix}
                                        </span>
                                      ) : null}
                                      <span
                                        style={{
                                          display: 'block',
                                          minWidth: 0,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {node.shortTitle || node.title || node.label}
                                      </span>
                                    </span>
                                  )}
                                />
                              ) : (
                                <Select
                                  id="ingredient_id"
                                  name="ingredient_id"
                                  className="ingredient-select"
                                  mode="multiple"
                                  value={values.ingredient_id}
                                  onChange={(vals, options) => {
                                  // Always clear immediately so stale info never shows for a different ingredient
                                  setIngredientRemainingInfo(null);
                                  setFieldValue('ingredient_id', vals || []);
                                  setFieldValue('ingredient', options?.map((o) => o.label) || []);
                                  setFieldValue('unit', options?.length > 0 ? options[0].unit : null);
                                  setFieldValue('unit_id', options?.length > 0 ? options[0].unit_id : null);
                                  runCapacityCheck({
                                    ingredientId: vals?.length === 1 ? vals[0] : null,
                                    unitId: options?.length > 0 ? options[0].unit_id : null,
                                  });
                                  if (vals?.length === 1) {
                                    setFieldValue('alias_name', options[0]?.label || null);
                                    setFieldValue('recipe_bom_item_id', null);
                                    setFieldValue('bom_split_percent', null);
                                    if (recipe_id) {
                                      get_recipe_ingredient_remaining(
                                        (res) => {
                                          if (res?.data?.status === 'success') {
                                            setIngredientRemainingInfo(res.data.data);
                                          }
                                        },
                                        { recipe_id, material_id: vals[0] }
                                      );
                                    }
                                  } else {
                                    setFieldValue('alias_name', null);
                                    setFieldValue('recipe_bom_item_id', null);
                                    setFieldValue('bom_split_percent', null);
                                  }
                                }}
                                placeholder={`Select ${ingredientLabel}`}
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={ingredientOptions}
                                // label stays the plain string (drives the
                                // selected tag + the filter above unchanged);
                                // optionRender only restyles the dropdown row —
                                // name left, code + unit right, instead of bare text.
                                optionRender={(option) => (
                                  <div className="flex items-center justify-between gap-3 min-w-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Package className="w-3.5 h-3.5 text-gray-400 dark:text-slate-400 flex-shrink-0" />
                                      <span className="truncate">{option.data.label}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      {option.data.material_unique_id && (
                                        <span className="text-[11px] font-mono text-gray-400 dark:text-slate-400">
                                          {option.data.material_unique_id}
                                        </span>
                                      )}
                                      {option.data.unit && (
                                        <Tag className="text-[10px] leading-4 m-0">{option.data.unit}</Tag>
                                      )}
                                    </div>
                                  </div>
                                )}
                              />
                              )}
                              {ingredientRemainingInfo && values.ingredient_id?.length === 1 && (
                                <div
                                  style={{
                                    marginTop: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    flexWrap: 'nowrap',
                                    whiteSpace: 'nowrap',
                                    fontSize: 11,
                                    lineHeight: '18px',
                                  }}
                                >
                                  <span style={{ color: '#2563eb' }}>
                                    Cfg <b>{ingredientRemainingInfo.configured_quantity}</b>
                                  </span>
                                  <span style={{ color: '#9ca3af' }}>·</span>
                                  <span style={{ color: '#ea580c' }}>
                                    Used <b>{ingredientRemainingInfo.used_quantity}</b>
                                  </span>
                                  <span style={{ color: '#9ca3af' }}>·</span>
                                  <span style={{ color: ingredientRemainingInfo.remaining_quantity < 0 ? '#dc2626' : '#16a34a' }}>
                                    Rem <b>{ingredientRemainingInfo.remaining_quantity}</b>
                                  </span>
                                  <span style={{ color: '#6b7280' }}>{ingredientRemainingInfo.unit}</span>
                                </div>
                              )}
                            </Form.Item>
                          </Col>
                        ) : (
                          <Col span={8}>
                            <Form.Item
                              label={`${ingredientLabel} Name`}
                              validateStatus={touched.ingredient_name_manual && errors.ingredient_name_manual ? 'error' : ''}
                              help={touched.ingredient_name_manual && errors.ingredient_name_manual}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                placeholder={`Enter ${ingredientLabel.toLowerCase()} name`}
                                value={values.ingredient_name_manual}
                                onChange={(e) => setFieldValue('ingredient_name_manual', e.target.value || null)}
                              />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>

                      {(isManualNameType ? !!values.ingredient_name_manual : values.ingredient_id?.length === 1) && (
                        <Row gutter={16} style={{ marginTop: 8 }}>
                          <Col span={8}>
                            <Form.Item label="Alias Name" style={{ marginBottom: 0 }}>
                              <Input
                                placeholder="Enter alias name"
                                value={values.alias_name}
                                onChange={(e) => setFieldValue('alias_name', e.target.value || null)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* Row 2: Target Quantity + Allowed Deviation.
                          2026-09-02, "that batch size and batch % seems no necessary anymore
                          here / that quantity strategy maps to that material rule basis and
                          deviation based on tolerance in bom" — confirmed and acted on both:
                          "Quantity Strategy" removed outright (grepped the whole backend —
                          zero references anywhere; rule_type's own choices already cover the
                          same ground — FIXED_BATCH_QTY≈Fixed, PERCENT_WW/PER_MASS/etc.≈Scale
                          With Batch, QS_*≈QS/Balance — and actually drive computation, unlike
                          this dropdown ever did). is_percentage/deviation below now hide
                          whenever a BOM Rule is linked (rule_type OR recipe_bom_item_id set) —
                          per Process_Ingredients' own docstring, "the total quantity math lives
                          exclusively on Recipe_BOM_Item" once that's the case, and deviation is
                          now actively kept in sync server-side from the BOM line's own
                          tolerance_percent (bom_calculation.apply_bom_tolerance_to_deviation),
                          not just silently ignored the way is_percentage always was. */}
                      {(() => {
                        const hasActiveBomRule = !!(values.rule_type || values.recipe_bom_item_id);
                        return (
                      <Row gutter={16}>
                        <Col span={16}>
                          {(() => {
                            const isWIPM = values.material_type === 'WIPM' || values.material_type === 'Product';
                            const enteredQty = parseFloat(values.quantity) || 0;

                            // Is THIS row the recipe's configured input ingredient?
                            const selIds = Array.isArray(values.ingredient_id) ? values.ingredient_id : [];
                            const isConfiguredIngredient =
                              isInputBasedRecipe &&
                              configuredIngredientId != null &&
                              selIds.map(String).includes(String(configuredIngredientId));

                            // Single-counter model (matches the header exactly):
                            //   live = committed − (entered − this row's original qty)
                            // for both the configured ingredient and the master batch.
                            const hasRemInfo =
                              isConfiguredIngredient &&
                              ingredientRemainingInfo &&
                              values.ingredient_id?.length === 1 &&
                              !values.carry_over_type;
                            const liveRemaining =
                              hasRemInfo && committedConfigured !== null
                                ? parseFloat(
                                  (committedConfigured - (enteredQty - (parseFloat(editingRowConfiguredOriginal) || 0))).toFixed(4)
                                )
                                : null;
                            const unit = ingredientRemainingInfo?.unit || values.unit || '';
                            const isOver = liveRemaining !== null && liveRemaining < 0;

                            // Master batch remaining — only for NON-input-based recipes.
                            const masterRem =
                              !isInputBasedRecipe &&
                                committedMaster !== null &&
                                !values.carry_over_type
                                ? parseFloat(
                                  (committedMaster - (enteredQty - (parseFloat(editingRowMasterOriginal) || 0))).toFixed(4)
                                )
                                : null;
                            const isOverMaster = masterRem !== null && masterRem < 0;
                            const exceedsBomStdQty =
                              bomStdQtyLimit !== null &&
                              bomStdQtyLimit !== undefined &&
                              !values.carry_over_type &&
                              enteredQty > Number(bomStdQtyLimit) + 1e-6;
                            const bomRelativePercent =
                              bomStdQtyLimit !== null &&
                              bomStdQtyLimit !== undefined &&
                              Number(bomStdQtyLimit) > 0 &&
                              !values.carry_over_type
                                ? ((enteredQty / Number(bomStdQtyLimit)) * 100)
                                : null;
                            return (
                              <Form.Item
                                label="Target Quantity"
                                validateStatus={(touched.quantity && errors.quantity) || exceedsBomStdQty ? 'error' : ''}
                                help={
                                  (touched.quantity && errors.quantity)
                                  || (exceedsBomStdQty ? `Target quantity cannot exceed BOM Std Qty (${bomStdQtyLimit} ${activeBomItem?.recipe_unit_name || values.unit || ''}).` : null)
                                  // FRM-01 A4-2: a correction is attached and the computed quantity is pending
                                  || (values.correction_formula_id && (values.quantity === null || values.quantity === undefined || values.quantity === '')
                                    ? 'pending — correction formula not active' : null)
                                }
                                style={{ marginBottom: 0 }}
                              >
                                <Space.Compact className="w-full">
                                  <InputNumber
                                    className="w-full"
                                    placeholder={values.carry_over_type ? '↩ Carried Over' : `Qty ${values.unit ? `in ${values.unit}` : ''}`}
                                    min={0}
                                    step="any"
                                    value={values.carry_over_type ? null : values.quantity}
                                    disabled={!!values.carry_over_type}
                                    onChange={(val) => {
                                      if (onQuantityChange) onQuantityChange(val, values.quantity, values.ingredient_id);
                                      setFieldValue('quantity', val);
                                      runCapacityCheck({ quantity: val });
                                    }}
                                    addonAfter={values.unit}
                                    style={values.carry_over_type ? { backgroundColor: '#f0f9ff', color: '#0369a1' } : {}}
                                  />
                                  {!isWIPM && !hasActiveBomRule && (
                                    <Select
                                      value={values.is_percentage}
                                      onChange={(val) => setFieldValue('is_percentage', val)}
                                      style={{ width: 120 }}
                                      size='large'
                                      disabled={!!values.carry_over_type}
                                      options={[
                                        { value: false, label: 'Batch Size' },
                                        { value: true, label: '% of Batch' },
                                      ]}
                                    />
                                  )}
                                  {isWIPM && (
                                    <Tooltip title={`Reference a ${ingredientLabel.toLowerCase()} / previous operation as the source of this quantity`}>
                                      <Button
                                        type={values.carry_over_type ? 'primary' : 'dashed'}
                                        onClick={openCarryOverModal}
                                        style={{ flexShrink: 0 }}
                                      >
                                        {values.carry_over_type ? '↩ Carry Over ✓' : '↩ Carry Over'}
                                      </Button>
                                    </Tooltip>
                                  )}
                                </Space.Compact>
                                {liveRemaining !== null && (
                                  <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 11, color: '#6b7280' }}>Remaining after this:</span>
                                    <span style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: isOver ? '#f5222d' : '#52c41a',
                                    }}>
                                      {liveRemaining} {unit}
                                    </span>
                                    {isOver && (
                                      <span style={{ fontSize: 11, color: '#f5222d' }}>
                                        ({Math.abs(liveRemaining).toFixed(4)} {unit} over limit)
                                      </span>
                                    )}
                                  </div>
                                )}
                                {/* Master batch remaining warning (warn but allow) */}
                                {masterRem !== null && !activeBomItem && (
                                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 11, color: '#6b7280' }}>Batch remaining after this:</span>
                                    <span style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: isOverMaster ? '#f5222d' : '#52c41a',
                                    }}>
                                      {masterRem} {values.unit || ''}
                                    </span>
                                    {isOverMaster && (
                                      <span style={{ fontSize: 11, color: '#f5222d' }}>
                                        (exceeds batch quantity by {Math.abs(masterRem).toFixed(4)} {values.unit || ''})
                                      </span>
                                    )}
                                  </div>
                                )}
                                {carryOverSummary && (
                                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Tag color="purple">{carryOverSummary}</Tag>
                                    <Button size="small" type="link" danger onClick={clearCarryOver}>
                                      Clear
                                    </Button>
                                  </div>
                                )}
                                {bomStdQtyLimit !== null && bomStdQtyLimit !== undefined && (
                                  <Tooltip
                                    title={`BOM Std Qty: ${bomStdQtyLimit} ${activeBomItem?.recipe_unit_name || values.unit || ''}${bomRelativePercent !== null ? ` · ${bomRelativePercent.toFixed(2)}% of BOM total` : ''}`}
                                  >
                                    <div
                                      style={{
                                        marginTop: 4,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        maxWidth: '100%',
                                        fontSize: 11,
                                        lineHeight: '18px',
                                        color: exceedsBomStdQty ? '#f5222d' : '#6b7280',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: 'default',
                                      }}
                                    >
                                      <span>BOM:</span>
                                      <span style={{ fontWeight: 600, color: exceedsBomStdQty ? '#f5222d' : '#2563eb' }}>
                                        {bomStdQtyLimit} {activeBomItem?.recipe_unit_name || values.unit || ''}
                                      </span>
                                      {bomRelativePercent !== null && (
                                        <span style={{ color: exceedsBomStdQty ? '#f5222d' : '#6b7280' }}>
                                          · {bomRelativePercent.toFixed(2)}%
                                        </span>
                                      )}
                                    </div>
                                  </Tooltip>
                                )}
                              </Form.Item>
                            );
                          })()}
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label="Allowed Deviation"
                            validateStatus={touched.deviation && errors.deviation ? 'error' : ''}
                            help={
                              (touched.deviation && errors.deviation)
                              || (hasActiveBomRule
                                ? (values.recipe_bom_item_id && activeBomItem?.tolerance_percent == null
                                  ? 'No tolerance set on the linked BOM line yet — set it there, not here.'
                                  : 'Set on the linked BOM line, not here — see BOM Rule below.')
                                : null)
                            }
                            style={{ marginBottom: 0 }}
                          >
                            {hasActiveBomRule ? (
                              <InputNumber
                                className="w-full"
                                // 2026-09-02, "at least show here the deviation value at least" —
                                // values.deviation only reflects the BOM line's tolerance_percent
                                // AFTER a save round-trip (apply_bom_tolerance_to_deviation runs
                                // server-side, on save). Read the linked BOM line's real value
                                // directly here too, so it's correct immediately, not just after
                                // the next reload.
                                value={
                                  values.recipe_bom_item_id && activeBomItem?.tolerance_percent != null
                                    ? activeBomItem.tolerance_percent
                                    : values.deviation
                                }
                                disabled
                                addonAfter="%"
                              />
                            ) : (
                              <InputNumber
                                className="w-full"
                                placeholder="Deviation"
                                min={0}
                                step="any"
                                value={values.deviation}
                                disabled={isManualNameType}
                                onChange={(val) => setFieldValue('deviation', val)}
                                addonAfter="%"
                              />
                            )}
                          </Form.Item>
                        </Col>
                      </Row>
                        );
                      })()}

                      {/* ── BOM Rule Type (collapsible, optional) — when set, the server
                          auto-calculates Target Quantity above from this rule instead of
                          the manually-typed value. Leave collapsed/unset for a plain
                          manually-entered quantity, same as before this existed. ── */}
                      {!isManualNameType && (
                        <div className="mt-4 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider m-0">
                              BOM Rule
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                if (showBomRule) {
                                  setFieldValue('rule_type', null);
                                  setFieldValue('rule_value', null);
                                  setFieldValue('rule_uom_id', null);
                                  setFieldValue('recipe_variant_id', null);
                                  setFieldValue('correction_formula_id', null);
                                  setFieldValue('recipe_bom_item_id', null);
                                  setFieldValue('bom_split_percent', null);
                                  setFieldValue('target_parameter_id', null);
                                }
                                setShowBomRule(!showBomRule);
                              }}
                              className={`flex items-center justify-center rounded-full ${showBomRule ? 'param-btn-remove' : 'param-btn-add'}`}
                              style={{
                                width: 22, height: 22, flexShrink: 0,
                                backgroundColor: showBomRule ? '#fee2e2' : '#dbeafe',
                                border: `2px solid ${showBomRule ? '#f87171' : '#3b82f6'}`,
                                cursor: 'pointer',
                                outline: 'none',
                              }}
                            >
                              {showBomRule ? <MinusCircle className="w-3.5 h-3.5 text-red-500" /> : <PlusCircle className="w-3.5 h-3.5 text-blue-500" />}
                            </button>
                          </div>
                          {showBomRule && (
                            <>
                              {recipeBomItemList.length > 0 && (
                                <Row gutter={16} className="mb-2">
                                  <Col span={12}>
                                    <Form.Item
                                      label="Link to BOM Line"
                                      style={{ marginBottom: 0 }}
                                      help="The total quantity math lives on the recipe's BOM line — this operation just takes a share of it (Split %), so the same material can be split across multiple steps without double-counting."
                                    >
                                      <Select
                                        allowClear
                                        showSearch
                                        placeholder="Pick a recipe BOM item..."
                                        optionFilterProp="label"
                                        value={values.recipe_bom_item_id || undefined}
                                        onChange={(bomItemId) => {
                                          setIngredientRemainingInfo(null);
                                          if (!bomItemId) {
                                            setFieldValue('recipe_bom_item_id', null);
                                            setFieldValue('bom_split_percent', null);
                                            return;
                                          }
                                          const item = recipeBomItemList.find((b) => b.bom_item_id === bomItemId);
                                          if (!item) return;
                                          setFieldValue('ingredient_id', [item.material_inventory_id]);
                                          setFieldValue('recipe_bom_item_id', item.bom_item_id);
                                          setFieldValue('bom_split_percent', 100);
                                          // A BOM-linked ingredient takes its quantity from the BOM
                                          // line's own rule — independent rule fields are unused.
                                          setFieldValue('rule_type', null);
                                          setFieldValue('rule_value', null);
                                          setFieldValue('rule_uom_id', null);
                                          setFieldValue('recipe_variant_id', null);
                                          setFieldValue('correction_formula_id', null);
                                          setFieldValue('target_parameter_id', null);
                                        }}
                                        options={recipeBomItemList.map((b) => ({
                                          value: b.bom_item_id,
                                          label: `${b.material_name}${b.rule_type ? ` — ${RULE_TYPE_OPTIONS.find((o) => o.value === b.rule_type)?.label || b.rule_type}` : ''}`,
                                        }))}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              )}
                              {values.recipe_bom_item_id ? (
                                (() => {
                                  const bomItem = recipeBomItemList.find((b) => b.bom_item_id === values.recipe_bom_item_id);
                                  // FRM-01 A4-2: a pending correction on the line → no share to show (never a share of Std Qty)
                                  const correctionPending = !!bomItem && (bomItem.correction_pending === true
                                    || (!!bomItem.correction_formula_id && bomItem.corrected_quantity == null));
                                  const total = bomItem && !correctionPending ? (bomItem.corrected_quantity ?? bomItem.computed_quantity) : null;
                                  const splitQty = total !== null && total !== undefined && values.bom_split_percent
                                    ? (total * (Number(values.bom_split_percent) / 100)).toFixed(3)
                                    : null;
                                  return (
                                    <Row gutter={16}>
                                      <Col span={6}>
                                        <Form.Item label="Split % of BOM Line" style={{ marginBottom: 0 }}>
                                          <InputNumber
                                            className="w-full"
                                            min={0}
                                            max={100}
                                            placeholder="e.g. 60"
                                            addonAfter="%"
                                            value={values.bom_split_percent}
                                            onChange={(val) => setFieldValue('bom_split_percent', val)}
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={18} className="flex items-end pb-1">
                                        <span className="text-xs text-gray-500 dark:text-slate-400">
                                          BOM line total: {correctionPending ? 'pending — correction formula not active' : total !== null && total !== undefined ? `${total} ${bomItem?.recipe_unit_name || ''}` : '—'}
                                          {splitQty !== null && ` → this operation: ${splitQty} ${bomItem?.recipe_unit_name || ''}`}
                                          {' '}(saving validates the split doesn't push this BOM line's total allocation over 100% across all operations)
                                        </span>
                                      </Col>
                                    </Row>
                                  );
                                })()
                              ) : (
                                <>
                                  <Row gutter={16}>
                                    <Col span={8}>
                                      <Form.Item label="Rule Type" style={{ marginBottom: 0 }}>
                                        <Select
                                          allowClear
                                          placeholder="How this quantity is calculated"
                                          value={values.rule_type || undefined}
                                          onChange={(val) => setFieldValue('rule_type', val || null)}
                                          options={enabledRuleTypeOptions}
                                        />
                                      </Form.Item>
                                    </Col>
                                    {values.rule_type && (
                                      <Col span={5}>
                                        <Form.Item
                                          label={values.rule_type === 'QS_PROCESS' ? 'Typical Value (optional)' : QS_RULE_TYPES.includes(values.rule_type) ? 'Target' : 'Value'}
                                          style={{ marginBottom: 0 }}
                                        >
                                          <InputNumber
                                            className="w-full"
                                            placeholder="Value"
                                            value={values.rule_value}
                                            onChange={(val) => setFieldValue('rule_value', val)}
                                          />
                                        </Form.Item>
                                      </Col>
                                    )}
                                    {values.rule_type && values.rule_type !== 'QS_TARGET_PARAMETER' && values.rule_type !== 'QS_PROCESS' && (
                                      <Col span={5}>
                                        <Form.Item label="Unit" style={{ marginBottom: 0 }}>
                                          <Select
                                            showSearch
                                            placeholder="Unit"
                                            optionFilterProp="label"
                                            value={values.rule_uom_id || undefined}
                                            onChange={(val) => setFieldValue('rule_uom_id', val || null)}
                                            options={ruleUomOptions}
                                          />
                                        </Form.Item>
                                      </Col>
                                    )}
                                    {values.rule_type === 'QS_TARGET_PARAMETER' && (
                                      <Col span={6}>
                                        <Form.Item label="Target Parameter" style={{ marginBottom: 0 }}>
                                          <Select
                                            allowClear
                                            showSearch
                                            placeholder="e.g. pH, Concentration, Weight"
                                            optionFilterProp="label"
                                            value={values.target_parameter_id || undefined}
                                            onChange={(val) => setFieldValue('target_parameter_id', val || null)}
                                            options={(parameter_list || []).map((p) => ({
                                              label: p.parameter_name || p.name,
                                              value: p.id,
                                            }))}
                                          />
                                        </Form.Item>
                                      </Col>
                                    )}
                                    {PER_OUTPUT_UNIT_RULE_TYPES.includes(values.rule_type) && (
                                      <Col span={6}>
                                        <Form.Item label="Variant" style={{ marginBottom: 0 }}>
                                          <Select
                                            allowClear
                                            placeholder="Recipe variant"
                                            value={values.recipe_variant_id || undefined}
                                            onChange={(val) => setFieldValue('recipe_variant_id', val || null)}
                                            options={recipeVariantList.map((v) => ({
                                              label: v.name || `${v.value} ${v.uom_name || ''}`,
                                              value: v.variant_id,
                                            }))}
                                            notFoundContent="No variants on this recipe"
                                          />
                                        </Form.Item>
                                      </Col>
                                    )}
                                  </Row>
                                  <Row gutter={16} className="mt-2">
                                    <Col span={10}>
                                      <Form.Item
                                        label="Correction Formula"
                                        style={{ marginBottom: 0 }}
                                        help="Optional — potency, moisture, overage, or any other correction applied on top of the rule above"
                                      >
                                        <Select
                                          allowClear
                                          showSearch
                                          placeholder="No correction"
                                          optionFilterProp="label"
                                          value={values.correction_formula_id || undefined}
                                          onChange={(val) => setFieldValue('correction_formula_id', val || null)}
                                          options={activeFormulaList.map((f) => ({ label: f.name || f.code, value: f.id }))}
                                        />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* ── Conditional Parameter (collapsible) — kept within Material Definition ── */}
                      {has_monitoring_subop && <div className="mt-4 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>                  <div className="flex items-center gap-2 mb-2">
                        <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider m-0">
                          Conditional Parameter
                        </p>
                        <button
                          type="button"
                          className={`flex items-center justify-center rounded-full ${showConditionalParam ? 'param-btn-remove' : 'param-btn-add'}`}
                          style={{
                            width: 22, height: 22, flexShrink: 0,
                            backgroundColor: showConditionalParam ? '#fee2e2' : '#dbeafe',
                            border: `2px solid ${showConditionalParam ? '#f87171' : '#3b82f6'}`,
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background-color 0.2s, border-color 0.2s',
                          }}
                          onClick={() => {
                            if (showConditionalParam) {
                              setFieldValue('parameter_id', null);
                              setFieldValue('parameter', null);
                              setFieldValue('parameter_unit', null);
                              setFieldValue('min_value', null);
                              setFieldValue('max_value', null);
                            }
                            setShowConditionalParam(!showConditionalParam);
                          }}
                        >
                          {showConditionalParam
                            ? <MinusCircle size={14} style={{ color: '#ef4444' }} />
                            : <PlusCircle size={14} style={{ color: '#3b82f6' }} />
                          }
                        </button>
                      </div>
                        {showConditionalParam && (
                          <Row gutter={16}>
                            <Col span={values.parameter_id ? 16 : 24}>
                              <Form.Item
                                label="Parameter"
                                validateStatus={touched.parameter_id && errors.parameter_id ? 'error' : ''}
                                help={touched.parameter_id && errors.parameter_id}
                                style={{ marginBottom: 0 }}
                              >
                                <Select
                                  value={values.parameter_id}
                                  onChange={(val, option) => {
                                    setFieldValue('parameter_id', val || null);
                                    if (val) {
                                      setFieldValue('parameter', option.label);
                                    } else {
                                      setFieldValue('parameter', null);
                                      setFieldValue('parameter_unit', null);
                                      setFieldValue('min_value', null);
                                      setFieldValue('max_value', null);
                                    }
                                  }}
                                  showSearch
                                  placeholder="Select Parameter"
                                  allowClear
                                  optionFilterProp="children"
                                  filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                  }
                                  options={parameter_list?.map((param) => ({
                                    value: param.id,
                                    label: param.parameter_name,
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                            {values.parameter_id && (
                              <>
                                <Col span={4}>
                                  <Form.Item label="Min" style={{ marginBottom: 0 }}>
                                    <InputNumber className="w-full" placeholder="Min" step="any" value={values.min_value} onChange={(val) => setFieldValue('min_value', val)} />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="Max" style={{ marginBottom: 0 }}>
                                    <InputNumber className="w-full" placeholder="Max" step="any" value={values.max_value} onChange={(val) => setFieldValue('max_value', val)} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item label="Deviation Rule" style={{ marginBottom: 0 }}>
                                    <Select
                                      className="w-full"
                                      value={values.deviation_rule || 'none'}
                                      onChange={(val) => setFieldValue('deviation_rule', val)}
                                      options={DEVIATION_RULE_OPTIONS}
                                    />
                                  </Form.Item>
                                </Col>
                              </>
                            )}
                          </Row>
                        )}
                      </div>}
                    </div>

                    {/* ── Section: MATERIAL ROUTING & AUTOMATION ── */}
                    <div
                      className="mb-4 rounded-lg border bg-white dark:bg-slate-900"
                      style={{ borderColor: '#e5e7eb', padding: '16px 18px' }}
                    >
                      {sectionHeader('Material Routing & Automation')}
                      {(() => {
                        const ruleOn = !!one_machine_rule_enabled;
                        const hasSetup = (machine_setup_entries || []).length > 0;
                        if (ruleOn && !hasSetup) {
                          return (
                            <div
                              style={{
                                padding: 10,
                                marginBottom: 8,
                                background: '#fef2f2',
                                border: '1px dashed #fca5a5',
                                borderRadius: 6,
                                fontSize: 12,
                                color: '#991b1b',
                              }}
                            >
                              Please setup machines first in the Machine Setup tab. Machine selection is locked until then.
                            </div>
                          );
                        }
                        if (ruleOn && hasSetup) {
                          return (
                            <div
                              style={{
                                padding: 8,
                                marginBottom: 8,
                                background: '#eff6ff',
                                border: '1px solid #93c5fd',
                                borderRadius: 6,
                                fontSize: 12,
                                color: '#1e40af',
                              }}
                            >
                              <Lock className="inline w-3 h-3 mr-1" />
                              Machine type auto-selected from Machine Setup rule (One Machine Per Operation enabled).
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <Row gutter={16} className="mb-2">
                        {/* From Machine (Source Equipment) */}
                        <Col span={8}>
                          <Form.Item
                            label={
                              <span className="flex items-center gap-1">
                                Source Equipment
                                {sourceLockedByRule && (
                                  <Tooltip title="Locked — auto-selected from Machine Setup (One Machine Per Operation rule is enabled in operation type config)">
                                    <Lock className="w-3 h-3 text-yellow-600 cursor-help" />
                                  </Tooltip>
                                )}
                                {values.carry_over_type === 'current' &&
                                  values.carry_over_ref_ingredient_ids && (
                                    <Tooltip title={`Locked — auto-filled from the referenced ${ingredientLabel.toLowerCase()}'s To Machine (Carry Over). Change Material Type to clear.`}>
                                      <Lock className="w-3 h-3 text-indigo-600 cursor-help" />
                                    </Tooltip>
                                  )}
                              </span>
                            }
                            style={{ marginBottom: 0 }}
                          >
                            {sourceLockedByRule ? (
                              <LockedMachineSetupPanel
                                machines={setupMachineDetailsWithCapacity.machines}
                                groupLabel={setupMachineDetailsWithCapacity.groupLabel}
                                compact
                              />
                            ) : (
                              <>
                                <Cascader
                                  showSearch
                                  changeOnSelect
                                  allowClear={
                                    !(
                                      sourceLockedByCarryOver
                                    )
                                  }
                                  disabled={
                                    sourceLockedByCarryOver
                                  }
                                  placeholder="Select Machine Type"
                                  value={fromMachineType || undefined}
                                  style={{ width: '100%' }}
                                  popupClassName="cascader-wide-dropdown"
                                  dropdownMenuColumnStyle={{ minWidth: 220 }}
                                  displayRender={(labels) => labels.join(' / ')}
                                  notFoundContent={
                                    useUpstreamMachineOptions
                                      ? 'No machines used in the selected dependency operation'
                                      : 'No data'
                                  }
                                  options={sourceCascaderOptions}
                                  onChange={(val, selectedOptions) => {
                                    if (!val || val.length === 0) {
                                      if (isTransfer && transferAnchor === 'from') {
                                        resetTransferMachines();
                                      } else {
                                        clearMachineSide('from');
                                      }
                                      return;
                                    }
                                    const leaf = selectedOptions?.[selectedOptions.length - 1];
                                    const catId = leaf?._catId ?? val[0];
                                    const grpId = leaf?._grpId ?? val[1];
                                    const isGroupOnlySelection = Array.isArray(val) && val.length === 2;
                                    const machineId = isGroupOnlySelection ? null : (leaf?._machineId ?? null);
                                    const allIds = leaf?.value === '__all__'
                                      ? (leaf?._allIds || [])
                                      : (leaf?._allIds || (machineId ? [machineId] : []));
                                    const label = `${leaf?._catLabel ?? ''} / ${leaf?._grpLabel ?? ''}`;
                                    setFromMachineType(val);
                                    setFromMachineIds(allIds);
                                    setFieldValue('from_machine_id', machineId);
                                    setFieldValue('from_machine_ids', allIds);
                                    setFieldValue('from_machine_ids_csv', allIds.join(','));
                                    setFieldValue('from_machine_group_id', grpId);
                                    setFieldValue('from_machine_category_id', catId);
                                    setFieldValue('from_machine_name', label);
                                    if (isTransfer && one_machine_rule_enabled) {
                                      setTransferAnchor('from');
                                      applySetupMachineSide('to');
                                    }
                                  }}
                                />
                                {fromMachineIds.length > 0 && (
                                  <div className="flex items-center flex-wrap gap-1 mt-1.5">
                                    <Badge
                                      count={fromMachineIds.length}
                                      color="#204448"
                                      title={`${fromMachineIds.length} machine${fromMachineIds.length > 1 ? 's' : ''} selected`}
                                    />
                                    {resolveMachines(fromMachineIds).slice(0, 3).map((m) => (
                                      <Tooltip key={m.machine_id} title={machineChipTooltip(m)}>
                                        <Tag className="text-[10px] leading-4 m-0">
                                          {machineChipShort(m)}
                                        </Tag>
                                      </Tooltip>
                                    ))}
                                    {fromMachineIds.length > 3 && (
                                      <Tooltip title={resolveMachines(fromMachineIds).map((m) => machineChipTooltipText(m)).join(', ')}>
                                        <Tag className="text-[10px] leading-4 m-0">
                                          +{fromMachineIds.length - 3} more
                                        </Tag>
                                      </Tooltip>
                                    )}
                                  </div>
                                )}
                                {values.carry_over_type === 'previous' && upstreamSourceOperationNumbers.length > 0 && (
                                  <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>
                                    Machines from Op {upstreamSourceOperationNumbers.join(', Op ')}
                                  </div>
                                )}
                              </>
                            )}
                          </Form.Item>
                        </Col>
                        {/* To Machine (Destination Equipment) */}
                        <Col span={8}>
                          <Form.Item
                            label={
                              <span className="flex items-center gap-1">
                                Destination Equipment
                                {destinationLockedByRule && (
                                  <Tooltip title="Locked — auto-selected from Machine Setup (One Machine Per Operation rule is enabled in operation type config)">
                                    <Lock className="w-3 h-3 text-yellow-600 cursor-help" />
                                  </Tooltip>
                                )}
                              </span>
                            }
                            style={{ marginBottom: 0 }}
                          >
                            {destinationLockedByRule ? (
                              <LockedMachineSetupPanel
                                machines={setupMachineDetailsWithCapacity.machines}
                                groupLabel={setupMachineDetailsWithCapacity.groupLabel}
                                compact
                              />
                            ) : (
                              <>
                              <Cascader
                                showSearch
                                changeOnSelect
                                allowClear
                                placeholder="Select Machine Type"
                                value={toMachineType || undefined}
                                style={{ width: '100%' }}
                                popupClassName="cascader-wide-dropdown"
                                dropdownMenuColumnStyle={{ minWidth: 220 }}
                                displayRender={(labels) => labels.join(' / ')}
                                notFoundContent={
                                  isTransfer && (previous_operations || []).length
                                    ? 'No machines used in the selected dependency operation'
                                    : 'No data'
                                }
                                options={destinationCascaderOptions}
                                onChange={(val, selectedOptions) => {
                                  if (!val || val.length === 0) {
                                    if (isTransfer && transferAnchor === 'to') {
                                      resetTransferMachines();
                                    } else {
                                      clearMachineSide('to');
                                    }
                                    setCapacityCheck(null);
                                    return;
                                  }
                                  const leaf = selectedOptions?.[selectedOptions.length - 1];
                                  const catId = leaf?._catId ?? val[0];
                                  const grpId = leaf?._grpId ?? val[1];
                                  const isGroupOnlySelection = Array.isArray(val) && val.length === 2;
                                  const machineId = isGroupOnlySelection ? null : (leaf?._machineId ?? null);
                                  const allIds = leaf?.value === '__all__'
                                    ? (leaf?._allIds || [])
                                    : (leaf?._allIds || (machineId ? [machineId] : []));
                                  const label = `${leaf?._catLabel ?? ''} / ${leaf?._grpLabel ?? ''}`;
                                  setToMachineType(val);
                                  setToMachineIds(allIds);
                                  setFieldValue('to_machine_id', machineId);
                                  setFieldValue('to_machine_ids', allIds);
                                  setFieldValue('to_machine_ids_csv', allIds.join(','));
                                  setFieldValue('to_machine_group_id', grpId);
                                  setFieldValue('to_machine_category_id', catId);
                                  setFieldValue('to_machine_name', label);
                                  runCapacityCheck({ machineIds: allIds });
                                  if (isTransfer && one_machine_rule_enabled) {
                                    setTransferAnchor('to');
                                    applySetupMachineSide('from');
                                  }
                                }}
                              />
                              {toMachineIds.length > 0 && (
                                <div className="flex items-center flex-wrap gap-1 mt-1.5">
                                  <Badge
                                    count={toMachineIds.length}
                                    color="#204448"
                                    title={`${toMachineIds.length} machine${toMachineIds.length > 1 ? 's' : ''} selected`}
                                  />
                                  {resolveMachines(toMachineIds).slice(0, 3).map((m) => (
                                    <Tooltip
                                      key={m.machine_id}
                                      title={
                                        <div className="flex flex-col leading-5">
                                          {machineChipTooltip(m)}
                                          <span style={{ fontSize: 10, color: '#94a3b8' }}>
                                            Click to see how much / how many lots go through this machine
                                          </span>
                                        </div>
                                      }
                                    >
                                      <Tag
                                        onClick={() => previewMachineDistribution(m)}
                                        className="text-[10px] leading-4 m-0"
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {machineChipShort(m)}
                                      </Tag>
                                    </Tooltip>
                                  ))}
                                  {toMachineIds.length > 3 && (
                                    <Tooltip title={resolveMachines(toMachineIds).map((m) => machineChipTooltipText(m)).join(', ')}>
                                      <Tag className="text-[10px] leading-4 m-0">
                                        +{toMachineIds.length - 3} more
                                      </Tag>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                              </>
                            )}
                          </Form.Item>
                        </Col>
                        {/* Execution Mode — hidden entirely when the rule marks it hidden.
                            When visible, it's editable only if the rule allows it (else disabled). */}
                        {lockedRuleFields?.execution_mode_hidden !== true && (
                          <Col span={8}>
                            <Form.Item
                              label={<span className="flex items-center gap-1">Execution Mode {isRuleFieldLocked('execution_mode') && <Lock className="w-3 h-3 text-yellow-600" />}</span>}
                              validateStatus={touched.execution_mode && errors.execution_mode ? 'error' : ''}
                              help={touched.execution_mode && errors.execution_mode}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                value={values.execution_mode}
                                onChange={(val) => {
                                  setFieldValue('execution_mode', val);
                                  // Mirror the rule builder: switching mode clears methods
                                  // the new mode doesn't support (e.g. Automatic disallows QR/Barcode).
                                  const availability = METHOD_AVAILABILITY[val] || METHOD_AVAILABILITY.Manual;
                                  const cr = { ...(values.compliance_rules || {}) };
                                  if (!availability.qr_scan) cr.qr_scan = false;
                                  if (!availability.barcode) cr.barcode = false;
                                  if (val === 'Automatic') cr.scale_weight = true;
                                  setFieldValue('compliance_rules', cr);
                                }}
                                placeholder="Mode"
                                disabled={isRuleFieldLocked('execution_mode')}
                                options={[
                                  { value: 'Manual', label: 'Manual' },
                                  { value: 'Semi-Automatic', label: 'Semi-Auto' },
                                  { value: 'Automatic', label: 'Automatic' },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {/* Critical RM (CMA) now lives inline next to the QR VERIFIED
                            badge at the top of the Material Definition section. */}
                      </Row>

                      {/* ── Material Capacity Check — does this row's quantity fit the
                          destination machine(s)' Usable Capacity? See runCapacityCheck.
                          fits=null means genuinely not computable yet (missing
                          machine/material/capacity config), not a warning. ── */}
                      {capacityCheckLoading && (
                        <div className="text-xs text-gray-400 dark:text-slate-400 mb-1">Checking machine capacity…</div>
                      )}
                      {capacityCheck && (
                        <Row gutter={16} className="mb-2">
                          <Col span={24}>
                            {capacityCheck.fits === null ? (
                              <Alert type="info" showIcon message={capacityCheck.reason} style={{ fontSize: 12 }} />
                            ) : capacityCheck.fits ? (
                              <Alert
                                type="success"
                                showIcon
                                message={`Fits — ${capacityCheck.required_quantity_in_capacity_uom} of ${capacityCheck.usable_capacity.value} ${capacityCheck.usable_capacity.uom_name} ${capacityCheck.usable_capacity.spec_name} on ${capacityCheck.usable_capacity.machine_name}.`}
                              />
                            ) : values.lot_strategy === 'Multi Lot' ? (
                              (() => {
                                const effectiveLots = values.number_of_lots || capacityCheck.minimum_lots_needed;
                                const avgPerLot = capacityCheck.required_quantity_in_capacity_uom / effectiveLots;
                                const lotsSufficient = avgPerLot <= capacityCheck.usable_capacity.value;
                                return (
                                  <Alert
                                    type="warning"
                                    showIcon
                                    message={
                                      <div>
                                        <div>
                                          {/* 2026-09-02, "why only show bighopper3" — this number is deliberately
                                              the worst case (the tightest of every selected machine, since that's
                                              what decides whether ANY single-machine lot can work at all) — the real
                                              per-machine plan is the Lot Distribution panel below. "not this click
                                              to drop part" — the name here stays plain text; the clickable surface
                                              the user wants lives on the machine chips below Destination Equipment
                                              instead (view-only there, not removal). */}
                                          Exceeds {capacityCheck.usable_capacity.spec_name} on {capacityCheck.usable_capacity.machine_name}
                                          {' '}— the tightest of your {toMachineIds.length} selected machine{toMachineIds.length > 1 ? 's' : ''} (
                                          {capacityCheck.required_quantity_in_capacity_uom} / {capacityCheck.usable_capacity.value}{' '}
                                          {capacityCheck.usable_capacity.uom_name}) — split across at least{' '}
                                          {capacityCheck.minimum_lots_needed} lot{capacityCheck.minimum_lots_needed > 1 ? 's' : ''}.
                                          See Lot Distribution in Charging Strategy for the full per-machine split.
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                          <span className="text-xs font-medium">Number of Lots</span>
                                          <InputNumber
                                            size="small"
                                            min={1}
                                            precision={0}
                                            step={1}
                                            style={{ width: 90 }}
                                            value={effectiveLots}
                                            onChange={(val) => {
                                              const next = val || null;
                                              setFieldValue('number_of_lots', next);
                                              // "what about bottom part changing lots? then right side should
                                              // adjust as per that" — re-run so Lot Distribution reflects this
                                              // exact count instead of quietly drifting from it.
                                              runCapacityCheck({ numberOfLots: next });
                                            }}
                                          />
                                          <span className="text-xs text-gray-500 dark:text-slate-400">
                                            ≈ {avgPerLot.toFixed(3)} {capacityCheck.usable_capacity.uom_name} / lot
                                            {!lotsSufficient && (
                                              <span className="text-red-600 font-medium"> — still exceeds capacity, increase lots</span>
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    }
                                  />
                                );
                              })()
                            ) : (
                              <Alert
                                type="error"
                                showIcon
                                message={
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <span>
                                      Exceeds {capacityCheck.usable_capacity.spec_name} on {capacityCheck.usable_capacity.machine_name} (
                                      {capacityCheck.required_quantity_in_capacity_uom} / {capacityCheck.usable_capacity.value}{' '}
                                      {capacityCheck.usable_capacity.uom_name}). Switch to Multi Lot, or reduce the quantity to{' '}
                                      {capacityCheck.usable_capacity.value} {capacityCheck.usable_capacity.uom_name} or below.
                                    </span>
                                    <Button size="small" onClick={() => setFieldValue('lot_strategy', 'Multi Lot')}>
                                      Switch to Multi Lot
                                    </Button>
                                  </div>
                                }
                              />
                            )}
                          </Col>
                        </Row>
                      )}
                    </div>

                    {/* ── Section: OPERATOR INSTRUCTIONS ── */}
                    <div
                      className="mb-4 rounded-lg border bg-white dark:bg-slate-900"
                      style={{ borderColor: '#e5e7eb', padding: '16px 18px' }}
                    >
                      {sectionHeader('Operator Instructions')}
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            label={<span className="flex items-center gap-1">Instruction {lockedRuleFields?.instruction && <Lock className="w-3 h-3 text-yellow-600" />}</span>}
                            validateStatus={touched.instruction && errors.instruction ? 'error' : ''}
                            help={touched.instruction && errors.instruction}
                            style={{ marginBottom: 0 }}
                          >
                            <TextArea
                              rows={2}
                              placeholder="Instruction"
                              value={values.instruction}
                              disabled={!!lockedRuleFields?.instruction}
                              onChange={(e) => setFieldValue('instruction', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </div>

                </div>

                {/* ===== RIGHT: Compliance + Flow Panels — only rendered when rulesEnabled ===== */}
                {rulesEnabled && (
                  <div className="w-1/3 min-w-0 flex-shrink-0 flex flex-col">
                    {/* ── Rules (RM Code / Qty toggles) — only when QR Verification is active ── */}
                    {(() => {
                      const qrVerification = appSettings?.find((s) => s?.module === 'QR Code Verification' && s?.data_type === 'boolean');
                      const qrEnabled = qrVerification?.status === true;
                      if (!qrEnabled) return null;
                      const manualRules = [
                        { label: 'Material Code', complianceKey: 'verify_material_code', legacyField: 'is_rm_code', value: complianceVal('verify_material_code') },
                        { label: 'Quantity', complianceKey: 'verify_qty_in_range', legacyField: 'is_quantity', value: complianceVal('verify_qty_in_range') },
                      ];
                      return (
                        <div
                          className="rounded-lg border w-full mb-3"
                          style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '10px 14px' }}
                        >
                          <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mb-2">
                            QR Verification
                          </p>
                          <div className="flex flex-col gap-1">
                            {manualRules.map((rule) => (
                              <div key={rule.complianceKey} className="flex items-center justify-between py-0.5">
                                <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{rule.label}</span>
                                <Switch
                                  size="small"
                                  checked={rule.value}
                                  onChange={(val) => {
                                    const updated = { ...(values.compliance_rules || {}), [rule.complianceKey]: val };
                                    setFieldValue('compliance_rules', updated);
                                    setFieldValue(rule.legacyField, val);
                                  }}
                                  checkedChildren="Yes"
                                  unCheckedChildren="No"
                                  style={{ backgroundColor: rule.value ? '#10b981' : undefined }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── Verification Methods ── */}
                    <div
                      className="rounded-lg border w-full mb-3"
                      style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', padding: '12px 14px' }}
                    >
                      <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mb-3">
                        Verification Methods
                      </p>
                      <div className="flex flex-col gap-1">
                        {[
                          { key: 'qr_scan', label: 'QR Scan' },
                          { key: 'barcode', label: 'Barcode' },
                          { key: 'scale_weight', label: 'Scale / Weight' },
                        ]
                          .filter((m) => lockedRuleFields?.[`${m.key}_hidden`] !== true)
                          .map(({ key, label }) => {
                            const available = isMethodAvailable(key);
                            const locked = !available || isRuleFieldLocked(key) || (key === 'scale_weight' && values.execution_mode === 'Automatic');
                            const isOn = key === 'scale_weight' ? scaleOn : methodVal(key);
                            return (
                              <div
                                key={key}
                                className="flex items-center justify-between py-1"
                                style={{ cursor: locked ? 'not-allowed' : 'pointer', opacity: !available ? 0.4 : locked ? 0.75 : 1 }}
                                onClick={() => !locked && toggleMethod(key)}
                              >
                                <span className="text-sm font-medium text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                  {label}
                                  {!available && (
                                    <Tooltip title={`Not available in ${values.execution_mode} mode`}>
                                      <Lock className="w-3 h-3 text-gray-400 dark:text-slate-400" />
                                    </Tooltip>
                                  )}
                                  {available && locked && <Tooltip title="Locked by rule"><Lock className="w-3 h-3 text-yellow-600" /></Tooltip>}
                                </span>
                                <span style={{
                                  padding: '1px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                  color: isOn ? '#15803d' : '#6b7280',
                                  backgroundColor: isOn ? '#dcfce7' : '#f3f4f6',
                                }}>
                                  {!available ? 'N/A' : isOn ? 'YES' : 'NO'}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                      {/* Scale Machine Config — Group → multi-select Machine(s) → Process Controls.
                          2026-09-02, "make it for group basis and multi selection of machine not
                          one, in reality we can use multiple and runtime we use only one" —
                          several machines can be viable scales for this row; whichever is
                          physically available gets used at execution time. */}
                      {scaleOn && lockedRuleFields?.scale_weight_hidden !== true && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                          <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                            Scale · Machine Config
                          </div>
                          {isRuleFieldLocked('scale_machine_type_id') ? (
                            <div style={{ fontSize: 11, color: '#1d4ed8' }}>{lockedRuleFields?.scale_machine_label || '—'}</div>
                          ) : (
                            <>
                              <Cascader
                                showSearch
                                allowClear
                                changeOnSelect
                                placeholder="Machine Type → Group"
                                value={scaleCascaderValue}
                                style={{ width: '100%' }}
                                size="small"
                                popupClassName="cascader-wide-dropdown"
                                dropdownMenuColumnStyle={{ minWidth: 220 }}
                                displayRender={(labels) => labels.join(' / ')}
                                onChange={handleScaleGroupChange}
                                options={buildMachineGroupCascaderOptions(machineTypeWithGroups)}
                              />
                              {values.scale_machine_group_id && (
                                <div style={{ marginTop: 6 }}>
                                  <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                                    Machines ({scaleMachineIds.length} selected — any one may be used at runtime)
                                  </div>
                                  {scaleGroupMachines.length === 0 ? (
                                    <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No machines in this group</div>
                                  ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                      {scaleGroupMachines.map((m) => {
                                        const selected = scaleMachineIds.includes(m.machine_id);
                                        return (
                                          <Tooltip key={m.machine_id} title={machineChipTooltip(m)}>
                                            <div
                                              onClick={() => toggleScaleMachine(m.machine_id)}
                                              style={{
                                                padding: '3px 9px', fontSize: 11, fontWeight: 500,
                                                borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                                                border: `1.5px solid ${selected ? '#204448' : '#e2e8f0'}`,
                                                background: selected ? '#f0f4f4' : '#fff',
                                                color: selected ? '#204448' : '#64748b',
                                              }}
                                            >
                                              {machineChipShort(m)}
                                            </div>
                                          </Tooltip>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Process Controls — shown once at least one machine is picked */}
                              {scaleMachineIds.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                  <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                                    Process Controls
                                  </div>
                                  {scaleRegistersLoading ? (
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>Loading…</div>
                                  ) : scaleRegisters.length === 0 ? (
                                    <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No process controls attached to this machine (Machine → Equipment List → Edit Equipment)</div>
                                  ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                      {scaleRegisters.map((reg) => {
                                        const selected = (values.scale_process_controls || []).includes(reg.id);
                                        return (
                                          <div
                                            key={reg.id}
                                            onClick={() => {
                                              const curr = values.scale_process_controls || [];
                                              setFieldValue(
                                                'scale_process_controls',
                                                selected
                                                  ? curr.filter((id) => id !== reg.id)
                                                  : [...curr, reg.id],
                                              );
                                            }}
                                            style={{
                                              padding: '3px 9px', fontSize: 11, fontWeight: 500,
                                              borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                                              border: `1.5px solid ${selected ? '#204448' : '#e2e8f0'}`,
                                              background: selected ? '#f0f4f4' : '#fff',
                                              color: selected ? '#204448' : '#64748b',
                                            }}
                                          >
                                            {reg.sub_category_name || reg.name}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Verification Policy ── */}
                    <div
                      className="rounded-lg border w-full mb-3"
                      style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', padding: '12px 14px' }}
                    >
                      <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mb-3">
                        Verification Policy
                      </p>
                      <div className="flex flex-col gap-1">
                        {complianceRules.map((rule) => {
                          const locked = isComplianceRuleLocked(rule.key);
                          return (
                            <div
                              key={rule.key}
                              className="flex items-center justify-between py-1"
                              style={{ cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.75 : 1 }}
                              onClick={() => toggleCompliance(rule.key)}
                            >
                              <span className="text-sm font-medium text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                {rule.label}
                                {rule.desc && (
                                  <Tooltip title={rule.desc} placement="left">
                                    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      <Info className="w-3 h-3" style={{ color: '#94a3b8', cursor: 'help' }} />
                                    </span>
                                  </Tooltip>
                                )}
                                {locked && (
                                  <Tooltip title={`Locked by the assigned ${ingredientsAlias} Rule`}>
                                    <Lock className="w-3 h-3 text-yellow-600" />
                                  </Tooltip>
                                )}
                              </span>
                              <span style={{
                                minWidth: 40, textAlign: 'center', padding: '1px 10px', borderRadius: 12,
                                fontSize: 11, fontWeight: 600,
                                color: rule.yes ? '#15803d' : '#6b7280',
                                backgroundColor: rule.yes ? '#dcfce7' : '#f3f4f6',
                              }}>
                                {rule.yes ? 'YES' : 'NO'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Charging Strategy ── */}
                    <div
                      className="rounded-lg border w-full mb-3"
                      style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', padding: '12px 14px' }}
                    >
                      <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mb-3">
                        Charging Strategy
                      </p>
                      {methodVal('qr_scan') && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 5 }}>QR Charging</div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            {['Single QR', 'Multi QR'].map((val) => {
                              const active = values.charging_mode_qr === val;
                              return (
                                <div key={val} onClick={() => setFieldValue('charging_mode_qr', val)} style={{
                                  flex: 1, padding: '5px 8px', textAlign: 'center',
                                  border: `1.5px solid ${active ? '#204448' : '#e2e8f0'}`,
                                  borderRadius: 7, background: active ? '#f0f4f4' : '#fff',
                                  cursor: 'pointer', userSelect: 'none', fontSize: 11,
                                  fontWeight: 600, color: active ? '#204448' : '#6b7280',
                                }}>{val}</div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {scaleOn && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 5 }}>Scale Charging</div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            {['Single Stretch', 'Multi Stretch'].map((val) => {
                              const active = values.charging_mode_scale === val;
                              return (
                                <div key={val} onClick={() => setFieldValue('charging_mode_scale', val)} style={{
                                  flex: 1, padding: '5px 8px', textAlign: 'center',
                                  border: `1.5px solid ${active ? '#204448' : '#e2e8f0'}`,
                                  borderRadius: 7, background: active ? '#f0f4f4' : '#fff',
                                  cursor: 'pointer', userSelect: 'none', fontSize: 11,
                                  fontWeight: 600, color: active ? '#204448' : '#6b7280',
                                }}>{val}</div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 5 }}>Lot Strategy</div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          {[{ value: 'Single Lot', label: 'Single Lot' }, { value: 'Multi Lot', label: 'Multi Lot' }].map((opt) => {
                            const active = values.lot_strategy === opt.value;
                            return (
                              <div key={opt.value} onClick={() => setFieldValue('lot_strategy', opt.value)} style={{
                                flex: 1, padding: '5px 8px', textAlign: 'center',
                                border: `1.5px solid ${active ? '#204448' : '#e2e8f0'}`,
                                borderRadius: 7, background: active ? '#f0f4f4' : '#fff',
                                cursor: 'pointer', userSelect: 'none', fontSize: 11,
                                fontWeight: 600, color: active ? '#204448' : '#6b7280',
                              }}>{opt.label}</div>
                            );
                          })}
                        </div>
                      </div>
                      {/* ── Lot Distribution — real per-machine breakdown once the quantity
                          needs splitting (see runCapacityCheck / capacityCheck.distribution).
                          "so user can decide to take uniform lot size or leave it like that" —
                          the toggle re-runs the check with the other strategy; the plant
                          default (Application Settings → Uniform Lot Sizing) is what a fresh
                          material starts from. ── */}
                      {values.lot_strategy === 'Multi Lot' && capacityCheck?.fits === false && capacityCheck?.distribution && (() => {
                        const effectiveStrategy = values.fill_strategy || (uniformLotSizingDefault ? 'uniform' : 'max_fill');
                        // 2026-09-02, "per machine max and also last left after lots other
                        // than max" — the concrete subtraction for THIS case, not just the
                        // generic rule above it. The distribution algorithm only ever leaves
                        // at most one non-full lot (it stops as soon as remaining hits ~0), so
                        // this is safe to compute as "every full lot subtracted from the total".
                        const allLots = capacityCheck.distribution.machines.flatMap((m) =>
                          m.lots.map((l) => ({ ...l, machine_name: m.machine_name }))
                        );
                        const maxLots = allLots.filter((l) => l.is_full);
                        const remainderLots = allLots.filter((l) => !l.is_full);
                        return (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
                          <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                              Lot Distribution ({capacityCheck.distribution.total_lots} lot{capacityCheck.distribution.total_lots > 1 ? 's' : ''})
                            </span>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {[{ value: 'max_fill', label: 'Max Fill' }, { value: 'uniform', label: 'Uniform' }].map((opt) => {
                                const active = effectiveStrategy === opt.value;
                                return (
                                  <div
                                    key={opt.value}
                                    onClick={() => {
                                      setFieldValue('fill_strategy', opt.value);
                                      runCapacityCheck({ fillStrategy: opt.value });
                                    }}
                                    style={{
                                      padding: '2px 8px', textAlign: 'center',
                                      border: `1.5px solid ${active ? '#204448' : '#e2e8f0'}`,
                                      borderRadius: 6, background: active ? '#f0f4f4' : '#fff',
                                      cursor: 'pointer', userSelect: 'none', fontSize: 10,
                                      fontWeight: 600, color: active ? '#204448' : '#6b7280',
                                    }}
                                  >
                                    {opt.label}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* 2026-09-02, "for max we need all last value plus max(number of
                              lots-1) so user understand calculation" — Uniform is already
                              self-explanatory (every lot is the same size); Max Fill isn't,
                              so each lot is tagged (max) or (remainder) and this one-liner
                              spells out the rule once instead of per lot. */}
                          {effectiveStrategy === 'max_fill' && (
                            <p className="m-0" style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
                              Each machine filled to its own capacity (max) first; the last lot takes whatever's left (remainder).
                            </p>
                          )}
                          {effectiveStrategy === 'max_fill' && remainderLots.length === 1 && maxLots.length > 0 && (
                            <p className="m-0" style={{ fontSize: 10, color: '#64748b', marginBottom: 6, lineHeight: 1.5 }}>
                              {capacityCheck.required_quantity_in_capacity_uom} total
                              {maxLots.map((l) => ` − ${l.quantity} (${l.machine_name}, max)`).join('')}
                              {' '}= {remainderLots[0].quantity} ({remainderLots[0].machine_name}, remainder)
                            </p>
                          )}
                          {capacityCheck.distribution.machines.map((m) => (
                            <div
                              key={m.machine_id}
                              id={`lot-distribution-machine-${m.machine_id}`}
                              style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                                fontSize: 11, color: '#374151', marginBottom: 3, gap: 6, borderRadius: 4,
                                padding: '2px 4px', margin: '0 -4px 3px',
                                background: highlightedMachineId === m.machine_id ? '#fef3c7' : 'transparent',
                                transition: 'background 0.4s ease',
                              }}
                            >
                              <span style={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
                                {/* 2026-09-02, "why is there even drop option on right side, we are not
                                    dropping anything ... just utility part" — this whole panel (and the
                                    chips above it) is view-only: how much, how many lots, that's it. The
                                    "x" removal that used to live here is gone; nothing on this side ever
                                    changes Destination Equipment anymore. */}
                                <span>{m.machine_name} — {m.lots.length} lot{m.lots.length > 1 ? 's' : ''}</span>
                              </span>
                              <span style={{ color: '#6b7280', textAlign: 'right', flexShrink: 0 }}>
                                {m.lots.map((l, i) => (
                                  <span key={l.lot_number}>
                                    {i > 0 ? ' + ' : ''}
                                    {l.quantity}
                                    {effectiveStrategy === 'max_fill' && (
                                      <span style={{ color: '#9ca3af' }}> ({l.is_full ? 'max' : 'remainder'})</span>
                                    )}
                                  </span>
                                ))}
                                {' '}{capacityCheck.distribution.uom_name}
                              </span>
                            </div>
                          ))}
                          {capacityCheck.distribution.warning && (
                            <p className="m-0" style={{ fontSize: 10, color: '#dc2626', marginTop: 4 }}>
                              {capacityCheck.distribution.warning}
                            </p>
                          )}
                        </div>
                        );
                      })()}
                    </div>

                    {/* ── Runtime Execution Flow (static numbered steps) ── */}
                    {/* STATIC display-only — not persisted */}
                    <div
                      className="rounded-lg border w-full"
                      style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', padding: '12px 14px' }}
                    >
                      <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mb-3">
                        Runtime Execution Flow
                      </p>
                      <div className="flex flex-col gap-2">
                        {executionFlowSteps.map((step, idx) => (
                          <div key={step} className="flex items-center gap-2">
                            <Badge
                              count={idx + 1}
                              style={{ backgroundColor: '#3b82f6', flexShrink: 0 }}
                            />
                            <span className="text-sm text-gray-600 dark:text-slate-300">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Full-width footer — Save spans the whole card */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  disabled={
                    (!!one_machine_rule_enabled && (machine_setup_entries || []).length === 0)
                  }
                >
                  {editIndex !== null ? 'Update Material Execution Definition' : 'Save Material Execution Definition'}
                </Button>
                {!!one_machine_rule_enabled && (machine_setup_entries || []).length === 0 && (
                  <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6, textAlign: 'center' }}>
                    Please setup machines first in the Machine Setup tab before adding ingredients.
                  </div>
                )}
              </div>

              {/* ===== Carry Over Modal ===== */}
              <Modal
                title="Carry Over"
                open={carryOverModalOpen}
                onCancel={() => setCarryOverModalOpen(false)}
                onOk={confirmCarryOver}
                okText="Save"
                okButtonProps={{
                  disabled:
                    !stagedCarryType ||
                    (stagedCarryType === 'current' && !(stagedRefIng && stagedRefIng.length)) ||
                    (stagedCarryType === 'previous' && !(stagedPrevOp && stagedPrevOp.length)),
                }}
                destroyOnHidden
              >
                <Form layout="vertical">
                  <Form.Item label="Reference From" style={{ marginBottom: 12 }}>
                    <Select
                      allowClear
                      placeholder="Select reference"
                      value={stagedCarryType || undefined}
                      onChange={(val) => {
                        setStagedCarryType(val || null);
                        // Auto-pick dependency if only one exists
                        if (val === 'previous' && (previous_operations || []).length === 1) {
                          setStagedPrevOp([previous_operations[0].operation_number]);
                        } else {
                          setStagedPrevOp([]);
                        }
                        setStagedRefIng([]);
                      }}
                      options={[
                        { value: 'current', label: 'Current Operation' },
                        { value: 'previous', label: 'Previous Operation' },
                      ]}
                    />
                  </Form.Item>
                  {stagedCarryType === 'current' && (() => {
                    // Only ingredients added to THIS operation:
                    // 1. parent-provided prop `current_ingredients`
                    // 2. locally tracked additions from this session
                    const mergedMap = new Map();
                    (current_ingredients || []).forEach((ing) => {
                      if (ing?.ingredient_id) mergedMap.set(ing.ingredient_id, ing);
                    });
                    locallyAddedIngredients.forEach((ing) => {
                      if (!mergedMap.has(ing.ingredient_id)) mergedMap.set(ing.ingredient_id, ing);
                    });
                    // Exclude the ingredient being edited (can't reference itself)
                    const currentIngId = values.ingredient_id;
                    const merged = Array.from(mergedMap.values()).filter(
                      (ing) => String(ing.ingredient_id) !== String(currentIngId)
                    );
                    return (
                      <Form.Item label={`Reference ${ingredientLabel}`} style={{ marginBottom: 0 }}>
                        <Select
                          showSearch
                          allowClear
                          placeholder={`Select ${ingredientLabel.toLowerCase()}`}
                          mode="multiple"
                          // Normalise to strings so the selected value always matches an option
                          value={(stagedRefIng || []).map((v) => String(v))}
                          onChange={(val) => setStagedRefIng((val || []).map((v) => String(v)))}
                          optionFilterProp="label"
                          options={merged.map((ing) => ({
                            value: String(ing.ingredient_id),
                            label: ing.ingredient || ing.ingredient_name || `#${ing.ingredient_id}`,
                          }))}
                          notFoundContent={`No ${ingredientLabel.toLowerCase()} added to this operation yet`}
                        />
                        {merged.length === 0 && (
                          <div style={{ fontSize: 11, color: '#f97316', marginTop: 4 }}>
                            Add a {ingredientLabel.toLowerCase()} to this operation first, then open Carry Over to reference it.
                          </div>
                        )}
                      </Form.Item>
                    );
                  })()}
                  {stagedCarryType === 'previous' && (() => {
                    const filtered = [...(previous_operations || [])].sort(
                      (a, b) => Number(a.operation_number) - Number(b.operation_number)
                    );
                    return (
                      <Form.Item label="Previous Operation" style={{ marginBottom: 0 }}>
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select previous operation"
                          mode="multiple"
                          value={stagedPrevOp || []}
                          onChange={(val) => setStagedPrevOp(val || [])}
                          optionFilterProp="label"
                          options={filtered.map((op) => ({
                            value: op.operation_number,
                            label: op.operation_name
                              ? `${op.operation_name} (Op ${op.operation_number})`
                              : `Operation ${op.operation_number}`,
                          }))}
                          notFoundContent="No dependency operations selected for this operation"
                        />
                      </Form.Item>
                    );
                  })()}
                </Form>
              </Modal>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export const IngredientCard = ({ fromView, item, onEdit, onDelete }) => {
  const { isDark } = useTheme();
  const ingredientsAlias = useIngredientsAlias();
  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);

  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    });
  }, []);

  const {
    ingredient,
    ingredient_id,
    alias_name,
    quantity,
    unit,
    deviation,
    is_percentage,
    instruction,
    execution_mode,
    from_machine_name: _from_machine_name,
    to_machine_name: _to_machine_name,
    from_machine_id,
    from_machine_category_id,
    from_machine_group_id,
    to_machine_id,
    to_machine_category_id,
    to_machine_group_id,
    is_rm_code,
    is_quantity,
    _from_rule,
    sub_operation_type,
    material_type,
    is_work_in_progress_material,
    is_work_in_progress_quantity,
    work_in_progress_material_name,
    carry_over,
  } = item;

  // Critical RM — accept the flag from any of the shapes it's stored under.
  const isCriticalRm =
    item?.critical_rm ??
    item?.is_critical_rm ??
    item?.compliance_rules?.critical_rm ??
    false;

  const isWIPM = is_work_in_progress_material || material_type === 'WIPM';
  const displayIngredient = isWIPM ? (work_in_progress_material_name || (Array.isArray(ingredient) ? ingredient[0] : ingredient)) : (Array.isArray(ingredient) ? ingredient[0] : ingredient);

  // Resolve machine name from IDs if name not stored
  const resolveMachineName = (typeId, groupId) => {
    if (!typeId && !groupId) return null;
    // Try to find by category + group
    if (typeId) {
      const cat = machineTypeWithGroups.find((c) => c.machine_category_id === typeId);
      if (cat) {
        if (groupId) {
          const grp = cat.machine_groups?.find((g) => g.machine_group_id === groupId);
          return grp ? `${cat.machine_category_name} / ${grp.machine_group_name}` : cat.machine_category_name;
        }
        return cat.machine_category_name;
      }
    }
    // Fallback: groupId might be stored as the main id, search all groups
    const searchId = groupId || typeId;
    for (const cat of machineTypeWithGroups) {
      const grp = cat.machine_groups?.find((g) => g.machine_group_id === searchId);
      if (grp) return `${cat.machine_category_name} / ${grp.machine_group_name}`;
    }
    return null;
  };

  const from_machine_name =
    _from_machine_name ||
    resolveMachineName(from_machine_category_id || from_machine_id, from_machine_group_id);
  const to_machine_name =
    _to_machine_name ||
    resolveMachineName(to_machine_category_id || to_machine_id, to_machine_group_id);



  // Dark mode colors
  const cardBg = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const textColor = isDark ? '#ffffff' : '#111827';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#374151';
  const quantityBg = isDark ? '#1e3a5f' : '#eff6ff';
  const quantityText = isDark ? '#93c5fd' : '#1d4ed8';
  const quantityValue = isDark ? '#bfdbfe' : '#1e3a8a';
  const deviationBg = isDark ? '#4c1d95' : '#faf5ff';
  const deviationText = isDark ? '#c4b5fd' : '#7c3aed';
  const deviationValue = isDark ? '#ddd6fe' : '#4c1d95';
  const instructionBg = isDark ? defaultColors.palette.gray[700] : '#f9fafb';

  return (
    <div
      className="rounded-lg border shadow-sm hover:shadow-md transition p-3 text-sm"
      style={{ backgroundColor: cardBg, borderColor: borderColor }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div
            className="p-1.5 rounded-md flex-shrink-0"
            style={{ backgroundColor: isDark ? '#312e81' : '#e0e7ff' }}
          >
            <Package className="w-4 h-4" style={{ color: isDark ? '#a5b4fc' : '#4f46e5' }} />
          </div>
          <div className="min-w-0 flex-1">
            <Tooltip
              title={(() => {
                const ingArr = Array.isArray(ingredient) ? ingredient : (ingredient ? [ingredient] : []);
                const parts = [material_type, sub_operation_type].filter(Boolean).join(' ');
                if (ingArr.length > 0) {
                  const displayNames = alias_name ? [alias_name] : ingArr;
                  return (
                    <div className="flex flex-wrap gap-1">
                      {displayNames.map((name, i) => <Tag key={i} color="blue">{name}</Tag>)}
                    </div>
                  );
                }
                return parts || `ID: ${ingredient_id}`;
              })()}
              overlayInnerStyle={{
                backgroundColor: isDark ? defaultColors.palette.gray[700] : '#ffffff',
                color: isDark ? '#ffffff' : '#374151',
              }}
            >
              <div className="w-full overflow-x-auto overflow-y-hidden scrollbar-hide" style={{ cursor: 'grab' }}>
                <div className="flex gap-1 w-max">
                  {(() => {
                    const ingArr = Array.isArray(ingredient) ? ingredient : (ingredient ? [ingredient] : []);
                    const parts = [material_type, sub_operation_type].filter(Boolean).join(' ');
                    if (ingArr.length > 0) {
                      // Show alias_name in place of ingredient name when available
                      const displayNames = alias_name ? [alias_name] : ingArr;
                      return displayNames.map((name, i) => (
                        <Tag key={i} color="blue" className="max-w-[140px] truncate flex-shrink-0 font-semibold">
                          {parts ? `${parts} [${name.length > 12 ? name.slice(0, 10) + '…' : name}]` : name}
                        </Tag>
                      ));
                    }
                    return (
                      <p className="font-semibold leading-tight truncate text-base" style={{ color: textColor }}>
                        {parts || `ID: ${ingredient_id}`}
                      </p>
                    );
                  })()}
                </div>
              </div>
            </Tooltip>
          </div>
        </div>

        {_from_rule && (
          <Tooltip title="Locked — assigned from Operation Type rule">
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: '#fef9c3', color: '#92400e' }}>
              <Lock className="w-3 h-3" />
              Rule
            </div>
          </Tooltip>
        )}

        {!fromView && (
          <div className="flex items-center gap-1">
            <Tooltip
              title={`Edit ${ingredientsAlias}`}
              overlayInnerStyle={{
                backgroundColor: isDark ? defaultColors.palette.gray[700] : '#ffffff',
                color: isDark ? '#ffffff' : '#374151',
              }}
            >
              <button
                type="button"
                className="p-1.5 rounded-md transition-colors border border-transparent"
                onClick={() => onEdit()}
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            </Tooltip>
            <Tooltip
              title={`Delete ${ingredientsAlias}`}
              overlayInnerStyle={{
                backgroundColor: isDark ? defaultColors.palette.gray[700] : '#ffffff',
                color: isDark ? '#ffffff' : '#374151',
              }}
            >
              <button
                type="button"
                className="p-1.5 rounded-md transition-colors border border-transparent"
                onClick={() => onDelete()}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 my-2 flex-wrap">
        {sub_operation_type && <Tooltip title={`Movement Type: ${sub_operation_type}`}><Tag color="purple" className="text-xs" style={{ cursor: 'default' }}>{sub_operation_type}</Tag></Tooltip>}
        {material_type && (
          <Tooltip title={`Material Type: ${isWIPM ? 'Work In Progress Material' : 'Raw Material'}`}>
            <Tag color={isWIPM ? 'orange' : 'green'} className="text-xs" style={{ cursor: 'default' }}>
              {material_type}
            </Tag>
          </Tooltip>
        )}
        {execution_mode && <Tooltip title={`Execution Mode: ${execution_mode}`}><Tag color="blue" className="text-xs" style={{ cursor: 'default' }}>{execution_mode}</Tag></Tooltip>}
        {isCriticalRm && (
          <Tooltip title="This raw material is marked as critical">
            <Tag color="red" className="text-xs" style={{ cursor: 'default', fontWeight: 600 }}>
              Critical RM (CMA)
            </Tag>
          </Tooltip>
        )}
      </div>

      {/* Flags */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-xs" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>RM Code:</span>
          <Tag
            color={is_rm_code ? 'orange' : 'default'}
            className="text-xs m-0"
            style={{ lineHeight: '16px', padding: '0 4px' }}
          >
            {is_rm_code ? 'Yes' : 'No'}
          </Tag>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>Quantity:</span>
          <Tag
            color={is_quantity ? 'cyan' : 'default'}
            className="text-xs m-0"
            style={{ lineHeight: '16px', padding: '0 4px' }}
          >
            {is_quantity ? 'Yes' : 'No'}
          </Tag>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md p-2.5" style={{ backgroundColor: quantityBg }}>
          <p className="text-xs font-medium mb-1" style={{ color: quantityText }}>
            Quantity
          </p>
          {isWIPM && is_work_in_progress_quantity ? (
            <p className="text-xs font-medium" style={{ color: '#0369a1' }}>↩ Carried over from previous operation</p>
          ) : (
            <p className="font-semibold" style={{ color: quantityValue }}>
              {quantity}{' '}
              <span className="text-sm font-normal" style={{ color: quantityText }}>
                {unit || 'Unit'}
              </span>
            </p>
          )}
        </div>
        <div className="rounded-md p-2.5" style={{ backgroundColor: deviationBg }}>
          <p className="text-xs font-medium mb-1" style={{ color: deviationText }}>
            Deviation
          </p>
          <p className="font-semibold" style={{ color: deviationValue }}>
            {deviation}
            {is_percentage ? '%' : ''}
          </p>
        </div>

      </div>

      {/* Machine Routing */}
      {(from_machine_name || to_machine_name) && (
        <div className="mt-2 rounded-md p-2.5" style={{ backgroundColor: isDark ? '#1e1e2e' : '#faf5ff' }}>
          <p className="text-xs font-medium mb-1" style={{ color: isDark ? '#c4b5fd' : '#7c3aed' }}>
            Machine Routing
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {from_machine_name && <Tag color="geekblue" className="text-xs">{from_machine_name}</Tag>}
            {from_machine_name && to_machine_name && <span className="text-gray-400 dark:text-slate-400 text-xs">→</span>}
            {to_machine_name && <Tag color="volcano" className="text-xs">{to_machine_name}</Tag>}
          </div>
        </div>
      )}

      {/* Carry Over */}
      {carry_over && carry_over.type && (
        <div
          className="mt-2 rounded-md p-2.5"
          style={{
            backgroundColor: isDark ? '#312e81' : '#eef2ff',
            border: `1px solid ${isDark ? '#4338ca' : '#a5b4fc'}`,
          }}
        >
          <p
            className="text-xs font-medium mb-1"
            style={{ color: isDark ? '#a5b4fc' : '#3730a3' }}
          >
            ↩ Carry Over
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Tag color="purple" className="text-xs">
              {carry_over.type === 'current' ? 'Current Operation' : 'Previous Operation'}
            </Tag>
            {carry_over.type === 'current' && (carry_over.ref_ingredient_ids || []).length > 0 && (
              <Tag color="blue" className="text-xs">
                {ingredientsAlias}: {carry_over.ref_ingredient_name || carry_over.ref_ingredient_ids.join(', ')}
              </Tag>
            )}
            {carry_over.type === 'previous' && (carry_over.prev_operation_ids || []).length > 0 && (
              <Tag color="cyan" className="text-xs">
                Op {(carry_over.prev_operation_ids || []).join(', ')}
                {carry_over.prev_operation_name ? ` — ${carry_over.prev_operation_name}` : ''}
              </Tag>
            )}
          </div>
        </div>
      )}

      {/* Instruction */}
      {instruction && (
        <div className="mt-3 rounded-md p-2.5" style={{ backgroundColor: instructionBg }}>
          <p className="text-xs font-medium mb-1" style={{ color: secondaryTextColor }}>
            Instruction
          </p>
          <p className="text-sm leading-relaxed" style={{ color: textColor }}>
            {instruction}
          </p>
        </div>
      )}
    </div>
  );
};
